/**
 * KwikPass customer session for classic-account deprecation.
 * Reads kpToken (kp_data_sent / getKpToken), hydrates Shopify.customer,
 * and loads profile + orders via Storefront API.
 */
(function (window, document) {
  'use strict';

  var AES_KEY = 'kR7dmP9tnL2wjH5&vC8qxF4hwQ1iyT6t';
  var AES_IV = '2134765890213456';
  var DEFAULT_ORDER_IMAGE =
    'https://cdn.shopify.com/s/files/1/0888/8057/8807/files/default_order_image1.png?v=1767785335';

  var readyPromise = null;
  var readyResolvers = [];
  var customerRecord = null;
  var payloadRecord = null;
  var ordersCache = null;
  var ordersLoadError = null;
  var kpDetailsRecord = null;
  var kpDetailsError = null;

  function config() {
    return window.KpAccountConfig || {};
  }

  function accountPath() {
    var path =
      (window.merchantInfo && window.merchantInfo.customAccountPagePath) ||
      config().accountPath ||
      '/pages/account';
    return path.charAt(0) === '/' ? path : '/' + path;
  }

  function ordersPath() {
    return config().ordersPath || '/pages/orders';
  }

  function shopDomain() {
    return config().shopDomain || window.Shopify && window.Shopify.shop || window.location.hostname;
  }

  function apiVersion() {
    return config().storefrontApiVersion || '2024-10';
  }

  function storefrontToken() {
    return config().storefrontAccessToken || '';
  }

  function kpGraphqlUrl() {
    return config().kpGraphqlUrl || '';
  }

  /**
   * The KwikPass SDK stores the kp token under the cookie/localStorage name
   * returned by XGokwikToken() (KWIKUSERTOKEN in production), not under the
   * literal key "kpToken". Check the real names first, then the legacy one.
   */
  function kpTokenValue() {
    var names = [];
    try {
      if (typeof window.XGokwikToken === 'function') {
        names.push(window.XGokwikToken(window.merchantInfo && window.merchantInfo.environment));
      }
    } catch (e) {}
    names.push('KWIKUSERTOKEN', 'kpToken');
    for (var i = 0; i < names.length; i++) {
      if (!names[i]) continue;
      var value = readTokenValue(names[i]);
      if (value) {
        try { return decodeURIComponent(value); } catch (e) { return value; }
      }
    }
    return '';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * The API returns decimal rupee strings ("824.82"), not paise. Keep the
   * paise when they are non-zero so totals reconcile against the invoice.
   */
  function formatMoney(amount) {
    var n = Number(amount);
    if (Number.isNaN(n)) return '';
    var hasPaise = Math.round(n * 100) % 100 !== 0;
    return '₹' + n.toLocaleString('en-IN', {
      minimumFractionDigits: hasPaise ? 2 : 0,
      maximumFractionDigits: hasPaise ? 2 : 0
    });
  }

  /** Matches the Liquid `| date: '%b %d, %Y'` used across the account pages. */
  function formatDate(value) {
    if (!value) return '';
    var date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  var formatDateLong = formatDate;

  function numericId(gid) {
    if (gid == null) return '';
    var text = String(gid);
    var parts = text.split('/');
    return parts[parts.length - 1] || text;
  }

  /** Money objects arrive as { amount, currencyCode }; callers want the number. */
  function money(node) {
    if (node == null) return '';
    if (typeof node === 'number' || typeof node === 'string') return node;
    return node.amount == null ? '' : node.amount;
  }

  /** customAttributes is [{key,value}] — reshape to a plain lookup. */
  function attributesToMap(list) {
    var out = {};
    (list || []).forEach(function (item) {
      if (item && item.key != null) out[item.key] = item.value;
    });
    return out;
  }

  /** Liquid scans attribute KEYS for a substring (e.g. CASHBACK) and takes the value. */
  function findAttribute(map, needle) {
    for (var key in map) {
      if (Object.prototype.hasOwnProperty.call(map, key) && key.indexOf(needle) !== -1) {
        return map[key];
      }
    }
    return '';
  }

  /**
   * Exact port of main-order.liquid:1805-1820. Two things to preserve:
   * `contains` is case-SENSITIVE (the tag "Gokwik_cod_fees" must not match
   * 'COD'), and the final fallback is Prepaid — NOT COD. paymentGatewayNames is
   * deliberately unused; the Liquid never consults it, and letting it decide
   * mislabels orders whose tags say nothing.
   */
  function resolvePaymentMethod(tags, metafieldValue) {
    var joined = (tags || []).join(', ');
    var mfv = String(metafieldValue || '');
    if (joined.indexOf('COD') !== -1 || mfv === 'cashondelivery') return 'COD';
    if (joined.indexOf('UPI') !== -1 || mfv === 'upi') return 'UPI';
    if (joined.indexOf('Online') !== -1) return 'Online';
    return 'Prepaid';
  }

  /**
   * Legacy order URLs are /pages/order/<base64>, where the encoded value is
   * (order.id * 37) with the salt '3246k9sT!pL2Z9302' appended — see the Liquid
   * that used to resolve this in main-order.liquid. Kept so existing links from
   * transactional emails and bookmarks continue to resolve.
   */
  var LEGACY_ORDER_SALT = '3246k9sT!pL2Z9302';

  function orderIdFromLegacyPath() {
    var match = String(window.location.pathname || '').match(/\/pages\/order\/([^/?#]+)/);
    if (!match) return '';
    try {
      var token = decodeURIComponent(match[1]).replace(/-/g, '+').replace(/_/g, '/');
      var cleaned = atob(token).split(LEGACY_ORDER_SALT).join('').trim();
      var scaled = Number(cleaned);
      if (!isFinite(scaled) || scaled <= 0) return '';
      var id = scaled / 37;
      return Number.isInteger(id) ? String(id) : '';
    } catch (e) {
      return '';
    }
  }

  function getCookie(name) {
    var prefix = name + '=';
    var cookies = document.cookie.split(';');
    for (var i = 0; i < cookies.length; i++) {
      var part = cookies[i].trim();
      if (part.indexOf(prefix) === 0) {
        var value = part.slice(prefix.length);
        try {
          return decodeURIComponent(value);
        } catch (e) {
          return value;
        }
      }
    }
    return '';
  }

  function readStorage(name) {
    try {
      return window.localStorage.getItem(name) || '';
    } catch (e) {
      return '';
    }
  }

  function writeStorage(name, value) {
    try {
      if (value == null || value === '') window.localStorage.removeItem(name);
      else window.localStorage.setItem(name, String(value));
    } catch (e) {}
  }

  function readTokenValue(name) {
    return getCookie(name) || readStorage(name) || '';
  }

  function base64ToBytes(b64) {
    var binary = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }

  function decodeJwt(token) {
    var parts = String(token).split('.');
    if (parts.length < 2) return null;
    var json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  }

  function looksLikeEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  function looksLikeCiphertext(value) {
    var text = String(value || '').trim();
    if (!text || text.indexOf('@') !== -1 || text.length < 24 || text.length % 4 !== 0) return false;
    return /^[A-Za-z0-9+/]+={0,2}$/.test(text);
  }

  function looksLikePhone(value) {
    return /^\+?\d[\d\s-]{7,}$/.test(String(value || '').trim());
  }

  function displaySafe(value) {
    var text = String(value == null ? '' : value).trim();
    if (!text || looksLikeCiphertext(text)) return '';
    return text;
  }

  async function decryptAesToText(token) {
    if (!token || !window.crypto || !window.crypto.subtle) return '';
    try {
      var key = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(AES_KEY),
        { name: 'AES-CBC' },
        false,
        ['decrypt']
      );
      var decrypted = await crypto.subtle.decrypt(
        { name: 'AES-CBC', iv: new TextEncoder().encode(AES_IV) },
        key,
        base64ToBytes(token)
      );
      return new TextDecoder().decode(decrypted).replace(/\u0000+$/g, '').trim();
    } catch (e) {
      return '';
    }
  }

  async function decryptStoredValue(value) {
    var text = String(value || '').trim();
    if (!text) return '';
    if (!looksLikeCiphertext(text)) return text;
    var decrypted = await decryptAesToText(text);
    if (!decrypted) return '';
    try {
      var parsed = JSON.parse(decrypted);
      if (parsed && typeof parsed === 'object') {
        return parsed.email || parsed.phone || parsed.value || parsed.name || '';
      }
      if (typeof parsed === 'string') return parsed;
    } catch (e) {}
    return decrypted;
  }

  async function decryptAesToken(token) {
    var text = await decryptAesToText(token);
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      if (looksLikeEmail(text)) return { email: text };
      if (looksLikePhone(text)) return { phone: text };
      return text ? { value: text } : null;
    }
  }

  async function decodeKpToken(kpToken) {
    if (!kpToken) return null;
    if (typeof kpToken === 'object') return kpToken;
    var token = String(kpToken).trim();
    if (!token) return null;

    try {
      var aes = await decryptAesToken(token);
      if (aes && typeof aes === 'object') return aes;
    } catch (e) {}

    try {
      var jwt = decodeJwt(token);
      if (jwt && typeof jwt === 'object') return jwt;
    } catch (e) {}

    try {
      return JSON.parse(token);
    } catch (e) {}

    try {
      return JSON.parse(atob(token.replace(/-/g, '+').replace(/_/g, '/')));
    } catch (e) {}

    return { kpToken: token };
  }

  function normalizePayload(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var nested = raw.data || raw.payload || raw;
    var customerAccessToken =
      nested.customerAccessToken ||
      nested.customer_access_token ||
      nested.accessToken ||
      nested.access_token ||
      '';
    var customerId =
      nested.customerId ||
      nested.customer_id ||
      nested.shopifyCustomerId ||
      nested.shopify_customer_id ||
      nested.id ||
      '';
    var firstName = nested.firstName || nested.first_name || '';
    var lastName = nested.lastName || nested.last_name || '';
    var name =
      nested.name ||
      nested.displayName ||
      [firstName, lastName].filter(Boolean).join(' ').trim();

    return {
      email: nested.email || '',
      phone: nested.phone || nested.phoneNumber || nested.phone_number || '',
      firstName: firstName,
      lastName: lastName,
      name: name,
      customerId: numericId(customerId),
      customerGid: customerId && String(customerId).indexOf('gid://') === 0 ? customerId : customerId ? 'gid://shopify/Customer/' + numericId(customerId) : '',
      customerAccessToken: customerAccessToken,
      expiresAt: nested.expiresAt || nested.expires_at || '',
      raw: nested
    };
  }

  function currentAccessToken() {
    return (
      (payloadRecord && payloadRecord.customerAccessToken) ||
      readTokenValue('acs_tkn') ||
      readTokenValue('cap_tkn') ||
      ''
    );
  }

  function kwikSessionToken() {
    return readTokenValue('KWIKSESSIONTOKEN') || readTokenValue('ELRTOKEN') || '';
  }

  async function localKpIdentity() {
    // SDK writes this as "notifyph"; "notify_phone_number" is the legacy name.
    var phone = readTokenValue('notifyph') || readTokenValue('notify_phone_number') || '';
    if (phone && phone.indexOf('|') !== -1) {
      try {
        phone = atob(phone);
      } catch (e) {}
      var parts = String(phone).split('|');
      phone = parts[parts.length - 1] || phone;
    }
    phone = displaySafe(await decryptStoredValue(phone));
    if (phone && !looksLikePhone(phone)) phone = '';

    var email = displaySafe(await decryptStoredValue(readStorage('kp_email') || ''));
    if (email && !looksLikeEmail(email)) email = '';

    var storedId = readStorage('kp_customer_id') || '';
    if (!/^\d{5,}$/.test(String(storedId))) storedId = '';

    return {
      email: email,
      phone: phone,
      customerId: storedId
    };
  }

  function isLoggedIn() {
    return !!(
      currentAccessToken() ||
      (payloadRecord && payloadRecord.customerId) ||
      readStorage('kp_customer_id') ||
      readTokenValue('kpToken') ||
      kwikSessionToken()
    );
  }

  function redactToken(value) {
    var text = String(value == null ? '' : value);
    if (!text) return '';
    if (text.length <= 12) return text;
    return text.slice(0, 6) + '…[' + text.length + ']…' + text.slice(-4);
  }

  function snapshotObject(value, depth) {
    if (value == null) return value;
    if (typeof value !== 'object') return value;
    if (depth > 4) return '[nested]';
    var out = Array.isArray(value) ? [] : {};
    Object.keys(value).forEach(function (key) {
      var item = value[key];
      var lower = key.toLowerCase();
      if (typeof item === 'string' && (lower.indexOf('token') !== -1 || lower.indexOf('password') !== -1 || looksLikeCiphertext(item))) {
        out[key] = redactToken(item);
      } else if (item && typeof item === 'object') {
        out[key] = snapshotObject(item, depth + 1);
      } else {
        out[key] = item;
      }
    });
    return out;
  }

  function collectKpStorage() {
    var cookieNames = ['kpToken', 'acs_tkn', 'cap_tkn', 'sc_tkn', 'notify_phone_number', 'KWIKSESSIONTOKEN', 'ELRTOKEN', 'kp_email'];
    var cookies = {};
    cookieNames.forEach(function (name) {
      var value = getCookie(name);
      if (value) cookies[name] = looksLikeEmail(value) || looksLikePhone(value) ? value : redactToken(value);
    });
    var local = {};
    try {
      for (var i = 0; i < window.localStorage.length; i++) {
        var key = window.localStorage.key(i);
        if (!key) continue;
        if (/^(kp|acs_tkn|cap_tkn|sc_tkn|notify_|KWIK|ELRTOKEN|isLoggedIn)/i.test(key)) {
          var val = window.localStorage.getItem(key) || '';
          local[key] = looksLikeEmail(val) || looksLikePhone(val) ? val : redactToken(val);
        }
      }
    } catch (e) {}
    return { cookies: cookies, localStorage: local };
  }

  /**
   * Prints tokens and customer identity to the console — opt-in only.
   * Enable with ?kpdebug=1, or localStorage.setItem('kp_debug','1').
   */
  function debugEnabled() {
    try {
      if (/[?&]kpdebug=1/.test(window.location.search)) return true;
      return window.localStorage.getItem('kp_debug') === '1';
    } catch (e) {
      return false;
    }
  }

  /** Verbose tracing, off unless debugEnabled(). console.warn/error stay on. */
  function kpLog() {
    if (!debugEnabled()) return;
    console.log.apply(console, arguments);
  }

  async function logKpDebug(label, extra) {
    if (!debugEnabled()) return;
    extra = extra || {};
    var decodedKpToken = extra.decoded || null;
    var rawKpToken = readTokenValue('kpToken');
    if (!decodedKpToken && rawKpToken) {
      try {
        decodedKpToken = await decodeKpToken(rawKpToken);
      } catch (e) {
        decodedKpToken = { decodeError: String(e && e.message ? e.message : e) };
      }
    }
    var local = extra.local || (await localKpIdentity());
    var payload = extra.payload || payloadRecord;
    var customer = extra.customer || customerRecord;
    var report = {
      label: label,
      path: window.location.pathname,
      storefrontAccessTokenPresent: !!storefrontToken(),
      shopDomain: shopDomain(),
      sdkPresent: !!(window.__KP_LOGIN_SDK_INSTANCE__ || window.___KP_LOGIN_SDK_INSTANCE___),
      event: extra.event || null,
      decodedKpTokenKeys: decodedKpToken && typeof decodedKpToken === 'object' ? Object.keys(decodedKpToken) : [],
      decodedKpToken: snapshotObject(decodedKpToken, 0),
      normalizedPayload: snapshotObject(payload, 0),
      localKpIdentity: local,
      storefrontCustomer: customer
        ? {
            id: customer.id,
            firstName: customer.firstName,
            lastName: customer.lastName,
            displayName: customer.displayName,
            email: customer.email,
            phone: customer.phone,
            orderCount: ((((customer.orders || {}).edges) || []).length)
          }
        : null,
      ordersCount: (ordersCache || []).length,
      ordersLoadError: ordersLoadError,
      shopifyCustomer: window.Shopify && window.Shopify.customer,
      storage: collectKpStorage()
    };
    console.group('%c[KpAccount] ' + label, 'color:#0b6;font-weight:bold');
    kpLog(report);
    kpLog('KP decoded token:', decodedKpToken);
    kpLog('Normalized payload:', payload);
    kpLog('Storefront customer:', customer);
    kpLog('KP does not send a customer name. It sends email/phone/id. Name comes from Storefront API when a storefront token is configured.');
    console.groupEnd();
    window.__KP_ACCOUNT_LAST_DEBUG__ = report;
    return report;
  }

  async function applyPayload(payload) {
    if (payload) {
      var local = await localKpIdentity();
      payload.email = displaySafe(await decryptStoredValue(payload.email || '')) || local.email || '';
      payload.phone = displaySafe(await decryptStoredValue(payload.phone || '')) || local.phone || '';
      payload.name = displaySafe(payload.name);
      payload.firstName = displaySafe(payload.firstName);
      payload.lastName = displaySafe(payload.lastName);
      if (!payload.email && local.email) payload.email = local.email;
      if (!payload.phone && local.phone) payload.phone = local.phone;
      if (!payload.customerId && local.customerId) {
        payload.customerId = numericId(local.customerId);
        payload.customerGid = 'gid://shopify/Customer/' + payload.customerId;
      }
      if (looksLikeCiphertext(payload.customerId) || (payload.customerId && !/^\d{5,}$/.test(String(payload.customerId)))) {
        payload.customerId = local.customerId || '';
        payload.customerGid = payload.customerId ? 'gid://shopify/Customer/' + payload.customerId : '';
      }
    }
    payloadRecord = payload;
    if (!payload) return;

    if (payload.customerId && /^\d{5,}$/.test(String(payload.customerId))) {
      writeStorage('kp_customer_id', payload.customerId);
      window.kp_customer_id = payload.customerId;
    }
    if (payload.customerAccessToken) {
      try {
        window.localStorage.setItem('acs_tkn', payload.customerAccessToken);
      } catch (e) {}
    }

    hydrateShopifyCustomer(payload, customerRecord);
    hydrateDom();
    document.body.classList.add('kp-logged-in');
    document.body.classList.remove('kp-guest');
  }

  function hydrateShopifyCustomer(payload, customer) {
    window.Shopify = window.Shopify || {};
    // The Storefront customer record is null whenever storefront_access_token is
    // unset, which is the normal case here, and the KP token payload carries only
    // email/phone. customerDetails is then the one source of a real first/last
    // name. renderProfile already prefers it; the header has to as well, or it
    // falls through to the email below.
    var details = kpDetailsRecord;
    var id = (customer && numericId(customer.id)) || (details && numericId(details.id)) ||
             (payload && payload.customerId) || '';
    if (!/^\d{5,}$/.test(String(id))) id = '';
    var firstName = displaySafe((details && details.firstName) || (customer && customer.firstName) || (payload && payload.firstName) || '');
    var lastName = displaySafe((details && details.lastName) || (customer && customer.lastName) || (payload && payload.lastName) || '');
    var email = displaySafe((details && details.email) || (customer && customer.email) || (payload && payload.email) || '');
    var phone = displaySafe((details && details.phone) || (customer && customer.phone) || (payload && payload.phone) || '');
    var name = displaySafe(
      [firstName, lastName].filter(Boolean).join(' ').trim() ||
      (customer && customer.displayName) ||
      (payload && payload.name) ||
      // customerDetails is only fetched on the account page — deliberately, to
      // keep every other page at zero extra requests. The name it returned is
      // cached so the header can still greet by name everywhere else.
      readStorage('kp_customer_name') ||
      ''
    );
    if (!name) name = email || phone || 'Account';

    window.Shopify.customer = {
      id: id ? Number(id) || id : null,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      name: name,
      customerAccessToken: currentAccessToken()
    };

    if (window.__copyCodeCouponUi && id) {
      window.__copyCodeCouponUi.customerId = 'gid://shopify/Customer/' + id;
    }

    var zip = customer && customer.defaultAddress && customer.defaultAddress.zip;
    if (zip && String(zip).length === 6) {
      try {
        window.localStorage.setItem('customer_pincode', String(zip));
      } catch (e) {}
    }
  }

  /**
   * Last-resort display name. The KP token carries no name at all, and
   * customerDetails does not always have one either, so the only thing left is
   * the email — and the header should read "Vaibhav Gupta", not
   * "vaibhav.gupta@gmail.com". Returns '' when the local part has no letters
   * (e.g. an all-numeric alias), so the caller can fall back further.
   */
  function humanizeEmailName(email) {
    // Drop any +tag alias before splitting — it is routing, not part of a name.
    var local = String(email || '').split('@')[0].split('+')[0];
    if (!local) return '';
    var parts = local.split(/[._-]+/).filter(function (part) {
      return part && /[a-z]/i.test(part);
    });
    if (!parts.length) return '';
    return parts
      .map(function (part) {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      })
      .join(' ');
  }

  function setText(selector, value) {
    var safe = displaySafe(value);
    if (!safe) return;
    document.querySelectorAll(selector).forEach(function (el) {
      el.textContent = safe;
    });
  }

  function hydrateDom() {
    var customer = window.Shopify && window.Shopify.customer;
    if (!customer) return;
    var email = customer.email || '';
    var phone = customer.phone || '';
    // `Shopify.customer.name` falls back to the raw email so every other consumer
    // always has something to show. The header must not print an email, so it
    // uses the real name when one exists and a humanized email otherwise. This
    // is only ever a display string — nothing keys off it.
    var realName = displaySafe([customer.first_name, customer.last_name].filter(Boolean).join(' ').trim());
    var greetName = realName || humanizeEmailName(email);

    setText('[data-kp-customer-name]', greetName);
    setText('[data-kp-customer-email]', email);
    setText('[data-kp-customer-phone]', phone);
    setText('[data-kp-account-label]', 'Account');
    setText('.hdt-account-label', greetName || 'Account');
    // The greeting itself is unconditional for a logged-in session — gating it on
    // the name having resolved is what blanked it out in 6.7.0.
    document.querySelectorAll('[data-kp-header-welcome]').forEach(function (el) {
      el.textContent = 'Welcome back';
    });
    document.querySelectorAll('.hdt-brand-box[data-header_text_1]').forEach(function (el) {
      el.setAttribute('data-header_text_1', 'Welcome back');
      if (greetName) el.setAttribute('data-header_text_2', greetName);
    });
    setText('#icon-close-kwikpass .hdt-cart-label', greetName);

    document.querySelectorAll('input.hdt-newsletter__email').forEach(function (input) {
      if (email && !input.value) input.value = email;
    });

    document.querySelectorAll('[data-customer_id], [data-customer-id]').forEach(function (el) {
      if (customer.id) el.setAttribute(el.hasAttribute('data-customer_id') ? 'data-customer_id' : 'data-customer-id', String(customer.id));
    });

    document.querySelectorAll('[data-customer]').forEach(function (el) {
      if (email) el.setAttribute('data-customer', email);
    });
  }

  function clearSessionUi() {
    customerRecord = null;
    payloadRecord = null;
    ordersCache = null;
    ordersLoadError = null;
    kpDetailsRecord = null;
    window.Shopify = window.Shopify || {};
    window.Shopify.customer = null;
    document.body.classList.remove('kp-logged-in');
    document.body.classList.add('kp-guest');
    try {
      window.localStorage.removeItem('kp_customer_id');
      window.localStorage.removeItem('kp_email');
      window.localStorage.removeItem('kp_customer_name');
      window.localStorage.removeItem('isLoggedIn');
      window.localStorage.removeItem('customer_pincode');
    } catch (e) {}
  }

  function onApiError(error) {
    var status = error && (error.status || error.statusCode);
    if (status === 401 || status === 404) {
      if (typeof window.kpDeleteCookie === 'function') window.kpDeleteCookie('acs_tkn');
      try {
        window.localStorage.removeItem('acs_tkn');
        window.localStorage.removeItem('kpToken');
      } catch (e) {}
      window.alert('Your session has expired. Please log in again.');
      login();
      return true;
    }
    if (status === 403) {
      login();
      return true;
    }
    return false;
  }

  /** Either KwikPass entry point, whichever kwikpass.liquid has defined. */
  function kpLoginFn() {
    if (typeof window.kpHandleLogin === 'function') return window.kpHandleLogin;
    if (typeof window.handleKpAndShopifyLogin === 'function') return window.handleKpAndShopifyLogin;
    return null;
  }

  /**
   * Never send anyone to /account/login — classic customer accounts are
   * deprecated and that page is a dead end. kp-customer.js is deferred, so the
   * KwikPass helpers may not be defined yet when a section calls this on
   * DOMContentLoaded; wait for them briefly, then fall back to the account page
   * (which boots this script again) rather than to a broken login route.
   */
  function login(redirectUrl) {
    var target = redirectUrl || accountPath();

    var fn = kpLoginFn();
    if (fn) {
      fn(target);
      return;
    }

    waitUntil(kpLoginFn, 4000)
      .then(function () {
        var ready = kpLoginFn();
        if (ready) {
          ready(target);
          return;
        }
        throw new Error('kwikpass login unavailable');
      })
      .catch(function () {
        console.warn('[KpAccount] KwikPass login helpers never appeared; sending to account page');
        if (window.location.pathname !== accountPath()) {
          window.location.href = accountPath();
        }
      });
  }

  function logout() {
    if (typeof window.handleLogout === 'function') {
      window.handleLogout();
      return;
    }
    window.location.href = '/account/logout';
  }

  function requireLogin(redirectUrl) {
    if (isLoggedIn()) return true;
    login(redirectUrl || window.location.pathname + window.location.search);
    return false;
  }

  /**
   * Kwikpass customer GraphQL API (post-KP-login; no Shopify session).
   * POST <kpGraphqlUrl> with the kp token in x-kp-token.
   * Always HTTP 200 — success is "no errors[]", never response.ok.
   */
  async function kpGraphql(query, variables, operationName) {
    var url = kpGraphqlUrl();
    if (!url) throw { code: 'NO_ENDPOINT', message: 'KpAccountConfig.kpGraphqlUrl is not set' };

    var token = kpTokenValue();
    if (!token) throw { code: 'UNAUTHENTICATED', message: 'No kp token found (KWIKUSERTOKEN)' };

    var response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-kp-token': token },
      body: JSON.stringify({ query: query, variables: variables || {}, operationName: operationName })
    });

    var json = await response.json().catch(function () { return {}; });

    if (json.errors && json.errors.length) {
      var code = (json.errors[0].extensions && json.errors[0].extensions.code) || 'UNKNOWN';
      var error = { code: code, message: json.errors[0].message, errors: json.errors };
      // Map contract error codes onto the existing session-recovery handler.
      if (code === 'UNAUTHENTICATED') onApiError({ status: 401 });
      else if (code === 'FORBIDDEN') onApiError({ status: 403 });
      throw error;
    }

    if (!response.ok) {
      throw { code: 'HTTP_' + response.status, message: 'Request failed', body: json };
    }

    return json.data || {};
  }

  var KP_CUSTOMER_DETAILS_QUERY =
    'query CustomerProfile($customerId: ID!, $email: String!) {\n' +
    '  customerDetails(customerId: $customerId, email: $email) {\n' +
    '    id firstName lastName email phone tags createdAt numberOfOrders\n' +
    '    defaultAddress { name address1 address2 city province country zip phone }\n' +
    '    addresses { id name address1 city zip phone }\n' +
    '    metafields { key value namespace }\n' +
    '  }\n' +
    '}';

  /**
   * customerId + email come from the SDK-written localStorage keys
   * (kp_customer_id, and kp_email once decrypted by localKpIdentity).
   */
  async function fetchKpCustomerDetails(force) {
    if (kpDetailsRecord && !force) return kpDetailsRecord;

    var payload = payloadRecord || {};
    var local = await localKpIdentity();
    var customerId = numericId(payload.customerId || local.customerId || '');
    var email = payload.email || local.email || '';

    if (!/^\d{5,}$/.test(String(customerId)) || !email) {
      kpDetailsError = 'missing-identity';
      console.warn('[KpAccount] customerDetails skipped — need customerId + email', {
        customerId: customerId || null,
        hasEmail: !!email
      });
      return null;
    }

    kpDetailsError = null;
    try {
      var data = await kpGraphql(
        KP_CUSTOMER_DETAILS_QUERY,
        { customerId: customerId, email: email },
        'CustomerProfile'
      );
      kpDetailsRecord = data.customerDetails || null;
      if (!kpDetailsRecord) kpDetailsError = 'empty';
      if (kpDetailsRecord) {
        // The header already rendered with only the token payload to go on.
        // Cache the name so other pages can greet by name without repeating
        // this request, then re-hydrate now that a real name exists.
        var resolvedName = displaySafe(
          [kpDetailsRecord.firstName, kpDetailsRecord.lastName].filter(Boolean).join(' ').trim()
        );
        if (resolvedName) writeStorage('kp_customer_name', resolvedName);
        hydrateShopifyCustomer(payloadRecord, customerRecord);
        hydrateDom();
      }
      kpLog('[KpAccount] customerDetails', kpDetailsRecord);
      return kpDetailsRecord;
    } catch (error) {
      kpDetailsError = error.code || 'fetch-failed';
      console.warn('[KpAccount] customerDetails failed', error);
      throw error;
    }
  }

  /**
   * Verified against the live schema (introspection, Sep 2026): `metafields` on
   * both OrderSummary and Order is a flat [Metafield!]! of { namespace key value }
   * — there is NO metafield(namespace:, key:) accessor, so we fetch the list once
   * and index it here. `value` is always a String.
   */
  function metafieldMap(list) {
    var out = {};
    (list || []).forEach(function (m) {
      if (m && m.key != null) out[m.key] = m.value;
    });
    return out;
  }

  function mf(map, key) {
    var v = map && map[key];
    return v == null ? '' : v;
  }

  /** shipment_details and rrn_details are JSON documents stored as strings. */
  function mfJson(map, key) {
    var raw = mf(map, key);
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[KpAccount] metafield "' + key + '" is not valid JSON', e);
      return null;
    }
  }

  /**
   * shipment_details.shipments is keyed by tracking number. Flatten it into an
   * array while keeping the key, so callers do not have to re-derive it.
   */
  function normalizeShipmentDetails(raw, fulfillments) {
    if (!raw) return null;

    // shipment_details carries the tracking NUMBER (as the object key) but no
    // tracking URL. The only source for the URL is fulfillments.trackingInfo,
    // so index it by number and join. Drop this once shipment_details carries
    // a tracking_url of its own.
    var urlByNumber = {};
    (fulfillments || []).forEach(function (f) {
      (f && f.trackingInfo || []).forEach(function (ti) {
        if (ti && ti.number && ti.url) urlByNumber[String(ti.number)] = ti.url;
      });
    });

    var shipments = [];
    var map = raw.shipments || {};
    for (var trackingNumber in map) {
      if (!Object.prototype.hasOwnProperty.call(map, trackingNumber)) continue;
      var entry = map[trackingNumber] || {};
      shipments.push({
        trackingNumber: trackingNumber,
        trackingUrl: entry.tracking_url || urlByNumber[String(trackingNumber)] || '',
        status: String(entry.shipment_status || '').toUpperCase(),
        returnStatus: String(entry.return_status || '').toUpperCase(),
        fulfilledItems: entry.fulfilled_items || [],
        fulfillmentCreatedAt: entry.fulfillment_created_at || '',
        inTransitDate: entry.in_transit_date || '',
        outForDeliveryDate: entry.out_for_delivery_date || '',
        deliveryDate: entry.delivery_date || entry.delivered_date || '',
        returnDate: entry.return_date || '',
        expectedDeliveryDate: entry.expected_delivery_date || '',
        failedDeliveryReason: entry.failed_delivery_reason || '',
        latestTrackingNote: entry.latest_tracking_note || '',
        invoiceUrl: entry.invoice_url || '',
        display: entry.display
      });
    }
    return {
      shipmentCount: Number(raw.shipment_count) || 0,
      pendingItemsCount: Number(raw.pending_items_count) || 0,
      pendingItems: raw.pending_items || [],
      shipments: shipments
    };
  }

  /**
   * OrderSummary is deliberately thin — it has NO processedAt, cancelledAt,
   * cancelReason or currentSubtotalLineItemsQuantity. Anything the list card
   * needs beyond this comes from metafields or is derived in mapKpOrder.
   */
  /**
   * /cart/add.js is all-or-nothing: one dead variant fails the whole request.
   * Confirm the ordered variant still exists on the product before offering it.
   */
  function variantPurchasable(line) {
    var p = line && line.product;
    if (!p || !line.variantId) return false;
    if (p.status && String(p.status).toUpperCase() !== 'ACTIVE') return false;
    var variants = p.variants || [];
    // An empty variants array means the API did not return them, not that the
    // product has none — fall back to trusting variantId rather than blocking.
    if (!variants.length) return true;
    var want = numericId(line.variantId);
    return variants.some(function (v) { return numericId(v.id) === want; });
  }

  /**
   * Only link to products that are still ACTIVE — a handle for a draft or
   * archived product resolves to a 404 on the storefront.
   */
  function productUrlFrom(line) {
    var p = line && line.product;
    if (!p || !p.handle) return '';
    if (p.status && String(p.status).toUpperCase() !== 'ACTIVE') return '';
    return '/products/' + p.handle;
  }

  var RETURN_STATES = ['CLOSED', 'RETURNED', 'RETURN_CLOSE'];
  var RETURNED_SHIPMENT_STATES = ['RETURN_CLOSE', 'FAILED_TO_DELIVER'];

  /** DELIVERED only when every shipment is delivered, matching the Liquid. */
  function deliveryStatusFrom(shipment) {
    if (!shipment || !shipment.shipments || !shipment.shipments.length) return '';
    if (shipment.pendingItemsCount > 0) return '';
    var all = shipment.shipments.every(function (s) { return s.status === 'DELIVERED'; });
    return all ? 'DELIVERED' : '';
  }

  /** Latest delivery date across shipments. */
  function deliveredDateFrom(shipment) {
    if (!shipment || !shipment.shipments) return '';
    var best = '';
    shipment.shipments.forEach(function (s) {
      if (s.status !== 'DELIVERED' || !s.deliveryDate) return;
      if (!best || new Date(s.deliveryDate) > new Date(best)) best = s.deliveryDate;
    });
    return best;
  }

  function returnStatusFrom(shipment) {
    if (!shipment || !shipment.shipments) return '';
    var returned = shipment.shipments.some(function (s) {
      return RETURN_STATES.indexOf(s.returnStatus) !== -1 ||
             RETURNED_SHIPMENT_STATES.indexOf(s.status) !== -1;
    });
    return returned ? 'RETURNED' : '';
  }

  function returnDateFrom(shipment) {
    if (!shipment || !shipment.shipments) return '';
    var found = '';
    shipment.shipments.forEach(function (s) { if (s.returnDate && !found) found = s.returnDate; });
    return found;
  }

  var KP_ORDERS_QUERY =
    'query CustomerOrders($customerId: ID!, $email: String!, $first: Int, $after: String) {\n' +
    '  orders(customerId: $customerId, email: $email, first: $first, after: $after) {\n' +
    '    pageInfo { hasNextPage endCursor }\n' +
    '    edges { cursor node {\n' +
    '      id name createdAt processedAt note tags\n' +
    '      cancelledAt cancelReason\n' +
    '      currentSubtotalLineItemsQuantity\n' +
    '      displayFulfillmentStatus displayFinancialStatus\n' +
    '      totalPrice { amount currencyCode }\n' +
    '      metafields { namespace key value }\n' +
    '      customAttributes { key value }\n' +
    '      lineItems { id title quantity variantTitle sku imageUrl productId variantId }\n' +
    '    } }\n' +
    '  }\n' +
    '}';

  /**
   * Contract OrderSummary -> the order shape renderOrderList/renderOrderDetail
   * already consume, so the UI is unchanged.
   */
  function mapKpOrder(node) {
    if (!node) return null;
    var items = (node.lineItems || []).map(function (line) {
      return {
        id: line.id,
        title: line.title || '',
        quantity: line.quantity,
        image: line.imageUrl || '',
        sku: line.sku || '',
        variantTitle: line.variantTitle || '',
        productId: line.productId || '',
        variantId: line.variantId || '',
        productUrl: productUrlFrom(line)
      };
    });
    var m = metafieldMap(node.metafields);
    var shipment = normalizeShipmentDetails(mfJson(m, 'shipment_details'), node.fulfillments);
    var financial = node.displayFinancialStatus || '';
    var paymentLabel = financial === 'PAID' ? 'Prepaid' : (financial === 'PENDING' || financial === 'VOIDED' ? 'COD' : financial);
    var tags = node.tags || [];
    // OrderSummary has no cancelledAt — the metafield is the only signal here.
    var cancellationReason = mf(m, 'cancellation_reason');

    return {
      id: node.id,
      name: node.name,
      orderNumber: String(node.name || '').replace('#', ''),
      processedAt: node.processedAt || node.createdAt,
      createdAt: node.createdAt,
      canceledAt: node.cancelledAt || '',
      cancelled: !!node.cancelledAt,
      cancelReason: cancellationReason || node.cancelReason || '',
      note: node.note || '',
      tags: tags,
      isMigrated: tags.indexOf('migrated_from_magento') !== -1,
      financialStatus: financial,
      fulfillmentStatus: node.displayFulfillmentStatus || '',
      paymentLabel: paymentLabel,
      statusUrl: '',
      total: money(node.totalPrice),
      shipping: '',
      shippingAddress: {},
      itemCount: Number(node.currentSubtotalLineItemsQuantity) ||
                 items.reduce(function (sum, i) { return sum + (Number(i.quantity) || 0); }, 0),
      lineItems: items,
      fulfillments: [],
      attributes: attributesToMap(node.customAttributes),
      metafields: m,
      estimatedDeliveryDate: mf(m, 'estimated_delivery_date'),
      // delivery_status / delivered_date are not written on every order, so fall
      // back to the shipment statuses. Without this a delivered order drops
      // through to the "Delayed" branch and tells the customer the wrong thing.
      deliveryStatus: String(mf(m, 'delivery_status') || deliveryStatusFrom(shipment)).toUpperCase(),
      deliveredDate: mf(m, 'delivered_date') || deliveredDateFrom(shipment),
      returnStatus: String(mf(m, 'return_status') || returnStatusFrom(shipment)).toUpperCase(),
      // Raw metafield, so the cashback and Return / Exchange checks behave the
      // same if the detail page falls back to list data.
      returnStatusMeta: String(mf(m, 'return_status') || '').toUpperCase(),
      returnDate: mf(m, 'return_date') || returnDateFrom(shipment),
      rrnDetails: mfJson(m, 'rrn_details'),
      shipmentDetails: shipment,
      shipmentCount: shipment ? shipment.shipmentCount : 0,
      pendingItemsCount: shipment ? shipment.pendingItemsCount : 0
    };
  }

  // Matches the Liquid's `paginate customer.orders by 15`. Draining every page
  // up front meant a customer with 200 orders waited on ~14 sequential requests
  // before seeing anything — which defeats the point of the cursor.
  var KP_ORDERS_PAGE_SIZE = 15;
  var ordersPageInfo = { hasNextPage: false, endCursor: null };

  /** One page. Returns null when identity is missing, else {orders, hasNextPage, endCursor}. */
  async function fetchKpOrdersPage(after) {
    var payload = payloadRecord || {};
    var local = await localKpIdentity();
    var customerId = numericId(payload.customerId || local.customerId || '');
    var email = payload.email || local.email || '';

    if (!/^\d{5,}$/.test(String(customerId)) || !email) {
      console.warn('[KpAccount] orders skipped — need customerId + email');
      return null;
    }

    var data = await kpGraphql(
      KP_ORDERS_QUERY,
      { customerId: customerId, email: email, first: KP_ORDERS_PAGE_SIZE, after: after || null },
      'CustomerOrders'
    );
    var connection = data.orders;
    if (!connection) return null;

    var orders = [];
    (connection.edges || []).forEach(function (edge) {
      var mapped = mapKpOrder(edge && edge.node);
      if (mapped) orders.push(mapped);
    });
    var info = connection.pageInfo || {};
    return { orders: orders, hasNextPage: !!info.hasNextPage, endCursor: info.endCursor || null };
  }

  /** First page only — the rest is pulled by loadMoreOrders(). */
  async function fetchKpOrders() {
    var page = await fetchKpOrdersPage(null);
    if (!page) return null;
    ordersPageInfo = { hasNextPage: page.hasNextPage, endCursor: page.endCursor };
    kpLog('[KpAccount] orders page 1', { count: page.orders.length, more: page.hasNextPage });
    return page.orders;
  }

  var loadMoreInFlight = null;

  /** Appends the next page to ordersCache. Safe to call concurrently. */
  function loadMoreOrders() {
    if (loadMoreInFlight) return loadMoreInFlight;
    if (!ordersPageInfo.hasNextPage || !ordersPageInfo.endCursor) return Promise.resolve([]);
    loadMoreInFlight = fetchKpOrdersPage(ordersPageInfo.endCursor)
      .then(function (page) {
        loadMoreInFlight = null;
        if (!page) return [];
        ordersPageInfo = { hasNextPage: page.hasNextPage, endCursor: page.endCursor };
        ordersCache = (ordersCache || []).concat(page.orders);
        return page.orders;
      }, function (error) {
        loadMoreInFlight = null;
        throw error;
      });
    return loadMoreInFlight;
  }

  var KP_ORDER_DETAIL_QUERY =
    'query OrderDetail($customerId: ID!, $email: String!, $orderId: ID!) {\n' +
    '  orderDetail(customerId: $customerId, email: $email, orderId: $orderId) {\n' +
    '    id name createdAt cancelledAt cancelReason note phone tags\n' +
    '    displayFulfillmentStatus displayFinancialStatus\n' +
    '    returnStatus refundable\n' +
    '    currentSubtotalLineItemsQuantity paymentGatewayNames\n' +
    '    totalPrice         { amount currencyCode }\n' +
    '    subtotalPrice      { amount currencyCode }\n' +
    '    totalShippingPrice { amount currencyCode }\n' +
    '    totalDiscounts     { amount currencyCode }\n' +
    '    totalRefunded      { amount currencyCode }\n' +
    '    transactions { id kind status gateway createdAt processedAt amount { amount currencyCode } }\n' +
    '    metafields { namespace key value }\n' +
    '    customAttributes { key value }\n' +
    '    customer { firstName lastName email phone\n' +
    '               defaultAddress { name address1 address2 city province provinceCode country zip phone } }\n' +
    '    shippingAddress { name firstName lastName address1 address2 city province provinceCode\n' +
    '                      country countryCodeV2 zip phone formatted }\n' +
    '    lineItems { id title quantity variantTitle sku imageUrl productId variantId\n' +
    '                price { amount currencyCode } total { amount currencyCode }\n' +
    '                product { id handle status featuredImageUrl\n' +
    '                          variants { id title sku } }\n' +
    '                customAttributes { key value } }\n' +
    '    fulfillments { id createdAt deliveredAt displayStatus estimatedDeliveryAt\n' +
    '                   trackingInfo { number url company }\n' +
    '                   lineItems { id quantity lineItemId price { amount currencyCode } } }\n' +
    '    returns { id fulfillmentLineItemIds }\n' +
    '  }\n' +
    '}';

  /**
   * Contract Order -> the same shape mapKpOrder/mapOrder produce.
   * Note: the schema has no shippingAddress on Order (verified by
   * introspection), so only the name/phone the order's customer snapshot
   * carries can be filled; the street/city/zip lines stay empty.
   */
  function mapKpOrderDetail(node) {
    if (!node) return null;
    var items = (node.lineItems || []).map(function (line) {
      return {
        id: line.id,
        title: line.title || '',
        quantity: line.quantity,
        image: line.imageUrl || '',
        sku: line.sku || '',
        variantTitle: line.variantTitle || '',
        productId: line.productId || '',
        variantId: line.variantId || '',
        properties: line.customAttributes || [],
        price: money(line.price),
        total: money(line.total),
        productUrl: productUrlFrom(line),
        purchasable: variantPurchasable(line)
      };
    });
    var summed = items.reduce(function (sum, i) { return sum + (Number(i.quantity) || 0); }, 0);
    var customer = node.customer || {};
    var addr = node.shippingAddress || customer.defaultAddress || {};
    var m = metafieldMap(node.metafields);
    var attributes = attributesToMap(node.customAttributes);
    var shipment = normalizeShipmentDetails(mfJson(m, 'shipment_details'), node.fulfillments);
    var financial = node.displayFinancialStatus || '';
    var paymentLabel = financial === 'PAID' ? 'Prepaid' : (financial === 'PENDING' || financial === 'VOIDED' ? 'COD' : financial);
    var tags = node.tags || [];
    var refunds = (node.transactions || []).filter(function (t) {
      return String(t && t.kind || '').toUpperCase() === 'REFUND';
    });
    // status is available now — a failed refund should not be shown as refunded.
    var refundTxn = refunds.filter(function (t) {
      return String(t.status || '').toUpperCase() === 'SUCCESS';
    })[0] || refunds[0] || null;

    return {
      id: node.id,
      name: node.name,
      orderNumber: String(node.name || '').replace('#', ''),
      processedAt: node.createdAt,
      createdAt: node.createdAt,
      canceledAt: node.cancelledAt || '',
      cancelled: !!node.cancelledAt,
      cancelReason: mf(m, 'cancellation_reason') || node.cancelReason || '',
      note: node.note || '',
      tags: tags,
      isMigrated: tags.indexOf('migrated_from_magento') !== -1,
      financialStatus: financial,
      fulfillmentStatus: node.displayFulfillmentStatus || '',
      paymentLabel: paymentLabel,
      // paymentGatewayNames is native and beats the metafield when present.
      paymentMethod: resolvePaymentMethod(tags, mf(m, 'payment_method')),
      paymentGatewayNames: node.paymentGatewayNames || [],
      refundable: !!node.refundable,
      statusUrl: '',

      total: money(node.totalPrice),
      subtotal: money(node.subtotalPrice),
      shipping: money(node.totalShippingPrice),
      discounts: money(node.totalDiscounts),
      refunded: money(node.totalRefunded) || (refundTxn ? money(refundTxn.amount) : ''),
      refundedAt: refundTxn ? (refundTxn.processedAt || refundTxn.createdAt || '') : '',
      walletAmount: mf(m, 'wallet_amount'),
      // Liquid: wallet_amount metafield, else the NECTOR_USED_AMOUNT attribute.
      mamacashUsed: mf(m, 'wallet_amount') || attributes.NECTOR_USED_AMOUNT || '',

      shippingAddress: {
        name: addr.name || [addr.firstName, addr.lastName].filter(Boolean).join(' ') ||
              [customer.firstName, customer.lastName].filter(Boolean).join(' '),
        address1: addr.address1 || '',
        address2: addr.address2 || '',
        city: addr.city || '',
        province: addr.province || '',
        provinceCode: addr.provinceCode || '',
        country: addr.country || '',
        countryCode: addr.countryCodeV2 || '',
        formatted: addr.formatted || [],
        zip: addr.zip || '',
        company: '',
        phone: addr.phone || customer.phone || ''
      },
      phone: node.phone || addr.phone || customer.phone || '',

      itemCount: Number(node.currentSubtotalLineItemsQuantity) || summed,
      lineItems: items,
      fulfillments: node.fulfillments || [],
      returns: node.returns || [],
      attributes: attributes,
      metafields: m,
      refundRrn: attributes['Refund RRN'] || '',
      cashback: findAttribute(attributes, 'CASHBACK'),

      estimatedDeliveryDate: mf(m, 'estimated_delivery_date'),
      deliveryStatus: String(mf(m, 'delivery_status') || deliveryStatusFrom(shipment)).toUpperCase(),
      deliveredDate: mf(m, 'delivered_date') || deliveredDateFrom(shipment),
      outForDeliveryDate: mf(m, 'out_for_delivery_date'),
      // Order.returnStatus is native, but reports NO_RETURN even when a single
      // shipment came back — so let a real per-shipment return win.
      returnStatus: String(
        returnStatusFrom(shipment) || mf(m, 'return_status') || node.returnStatus || ''
      ).toUpperCase(),
      returnDate: mf(m, 'return_date') || returnDateFrom(shipment),
      // The raw return_status metafield. returnStatus above is widened to
      // RETURNED when any one shipment came back; the Return / Exchange CTA
      // (main-order.liquid:1709, 1755) reads only the metafield, so a delivered
      // + returned split order still offers it.
      returnStatusMeta: String(mf(m, 'return_status') || '').toUpperCase(),
      invoiceUrl: mf(m, 'invoice_url'),
      rrnDetails: mfJson(m, 'rrn_details'),
      shipmentDetails: shipment,
      shipmentCount: shipment ? shipment.shipmentCount : 0,
      pendingItemsCount: shipment ? shipment.pendingItemsCount : 0
    };
  }

  async function fetchKpOrderDetail(orderId) {
    var payload = payloadRecord || {};
    var local = await localKpIdentity();
    var customerId = numericId(payload.customerId || local.customerId || '');
    var email = payload.email || local.email || '';
    var id = numericId(orderId);

    if (!/^\d{5,}$/.test(String(customerId)) || !email || !id) {
      console.warn('[KpAccount] orderDetail skipped — need customerId + email + orderId');
      return null;
    }

    var data = await kpGraphql(
      KP_ORDER_DETAIL_QUERY,
      { customerId: customerId, email: email, orderId: id },
      'OrderDetail'
    );
    var mapped = mapKpOrderDetail(data.orderDetail);
    kpLog('[KpAccount] orderDetail', mapped);
    return mapped;
  }

  /** Put the richer detail record into ordersCache so renderOrderDetail finds it. */
  function cacheOrder(order) {
    if (!order) return;
    ordersCache = ordersCache || [];
    for (var i = 0; i < ordersCache.length; i++) {
      if (ordersCache[i].id === order.id || numericId(ordersCache[i].id) === numericId(order.id)) {
        ordersCache[i] = order;
        return;
      }
    }
    ordersCache.push(order);
  }

  async function storefrontQuery(query, variables) {
    var token = storefrontToken();
    var accessToken = currentAccessToken();
    if (!token) {
      console.warn('[KpAccount] Missing Storefront API access token. Add it in Theme settings → Gokwik → Storefront API access token.');
      return null;
    }
    var response = await fetch('https://' + shopDomain() + '/api/' + apiVersion() + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    });
    var json = await response.json().catch(function () { return {}; });
    if (!response.ok) {
      var err = { status: response.status, message: 'Storefront API request failed', body: json };
      if (response.status !== 401 && response.status !== 403) onApiError(err);
      throw err;
    }
    if (json.errors && json.errors.length) {
      var authError = json.errors.some(function (item) {
        var msg = String(item.message || '').toLowerCase();
        return msg.indexOf('customer') !== -1 && (msg.indexOf('access token') !== -1 || msg.indexOf('unauthenticated') !== -1);
      });
      if (authError) onApiError({ status: 401 });
      throw { status: 400, message: json.errors[0].message, body: json };
    }
    if (accessToken && json.data && json.data.customer === null) {
      onApiError({ status: 401 });
      throw { status: 401, message: 'Customer session expired' };
    }
    return json.data;
  }

  var CUSTOMER_QUERY = 'query kpCustomer($token: String!) {\n' +
    '  customer(customerAccessToken: $token) {\n' +
    '    id firstName lastName displayName email phone\n' +
    '    defaultAddress { firstName lastName name address1 address2 city province provinceCode zip country countryCodeV2 phone }\n' +
    '    addresses(first: 10) { edges { node { id firstName lastName name address1 address2 city province zip country phone } } }\n' +
    '    orders(first: 30, sortKey: PROCESSED_AT, reverse: true) {\n' +
    '      edges { node {\n' +
    '        id name orderNumber processedAt canceledAt cancelReason financialStatus fulfillmentStatus statusUrl\n' +
    '        currentTotalPrice { amount currencyCode }\n' +
    '        originalTotalPrice { amount currencyCode }\n' +
    '        totalShippingPrice { amount currencyCode }\n' +
    '        shippingAddress { name address1 address2 city province zip country phone }\n' +
    '        lineItems(first: 50) { edges { node { title quantity originalTotalPrice { amount } variant { image { url altText } product { handle onlineStoreUrl } } } } }\n' +
    '        successfulFulfillments { trackingCompany trackingInfo { number url } }\n' +
    '      } }\n' +
    '    }\n' +
    '  }\n' +
    '}';

  function mapLineItems(order) {
    var edges = (((order || {}).lineItems || {}).edges) || [];
    return edges.map(function (edge) {
      var node = edge.node || {};
      var variant = node.variant || {};
      var product = variant.product || {};
      return {
        title: node.title,
        quantity: node.quantity,
        image: variant.image && variant.image.url,
        productUrl: product.onlineStoreUrl || (product.handle ? '/products/' + product.handle : '')
      };
    });
  }

  function mapOrder(node) {
    var items = mapLineItems(node);
    var itemCount = items.reduce(function (sum, item) { return sum + (Number(item.quantity) || 0); }, 0);
    var financial = node.financialStatus || '';
    var paymentLabel = financial === 'PAID' ? 'Prepaid' : (financial === 'PENDING' || financial === 'VOIDED' ? 'COD' : financial);
    return {
      id: node.id,
      name: node.name,
      orderNumber: node.orderNumber,
      processedAt: node.processedAt,
      canceledAt: node.canceledAt,
      cancelReason: node.cancelReason,
      financialStatus: financial,
      fulfillmentStatus: node.fulfillmentStatus,
      paymentLabel: paymentLabel,
      statusUrl: node.statusUrl,
      total: node.currentTotalPrice && node.currentTotalPrice.amount,
      shipping: node.totalShippingPrice && node.totalShippingPrice.amount,
      shippingAddress: node.shippingAddress,
      itemCount: itemCount,
      lineItems: items,
      fulfillments: node.successfulFulfillments || []
    };
  }

  function customerGid() {
    var id =
      (payloadRecord && payloadRecord.customerId) ||
      (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) ||
      readStorage('kp_customer_id') ||
      '';
    id = numericId(id);
    if (!/^\d{5,}$/.test(String(id))) return '';
    return 'gid://shopify/Customer/' + id;
  }

  function honasaHeaders() {
    var headers = {
      'Content-Type': 'application/json',
      'x-source': 'web-app',
      'x-platform': 'web'
    };
    var gid = customerGid();
    if (gid) headers['X-Customer-Id'] = gid;
    return headers;
  }

  function mapHonasaOrder(item) {
    if (!item || typeof item !== 'object') return null;
    var lineItems = item.lineItems || item.line_items || item.items || [];
    if (lineItems.edges) {
      lineItems = lineItems.edges.map(function (edge) { return edge.node || edge; });
    }
    var mappedItems = (Array.isArray(lineItems) ? lineItems : []).map(function (line) {
      var image =
        (line.image && (line.image.url || line.image.src)) ||
        line.image_url ||
        (line.variant && line.variant.image && line.variant.image.url) ||
        '';
      return {
        title: line.title || line.name || line.product_title || '',
        quantity: line.quantity || line.qty || 1,
        image: image,
        productUrl: line.productUrl || line.url || ''
      };
    });
    var itemCount = mappedItems.reduce(function (sum, line) { return sum + (Number(line.quantity) || 0); }, 0) || Number(item.itemCount || item.item_count || mappedItems.length) || 1;
    var financial = item.financialStatus || item.financial_status || item.payment_status || '';
    return {
      id: item.id || item.orderId || item.order_id || item.name || '',
      name: item.name || item.orderName || item.order_name || item.order_number || item.orderNumber || '',
      orderNumber: item.orderNumber || item.order_number || '',
      processedAt: item.processedAt || item.processed_at || item.createdAt || item.created_at || item.order_date || '',
      canceledAt: item.canceledAt || item.cancelled_at || item.canceled_at || '',
      cancelReason: item.cancelReason || item.cancel_reason || '',
      financialStatus: financial,
      fulfillmentStatus: item.fulfillmentStatus || item.fulfillment_status || item.delivery_status || '',
      paymentLabel: financial,
      statusUrl: item.statusUrl || '',
      total: item.total || item.currentTotalPrice || (item.current_total_price && item.current_total_price.amount) || item.total_price || '',
      shipping: item.shipping || '',
      shippingAddress: item.shippingAddress || item.shipping_address || {},
      itemCount: itemCount,
      lineItems: mappedItems,
      fulfillments: item.successfulFulfillments || item.fulfillments || []
    };
  }

  async function fetchHonasaOrders() {
    var gid = customerGid();
    if (!gid) {
      console.warn('[KpAccount] No numeric Shopify customer id — cannot call Mamaearth order API');
      return [];
    }
    var urls = [
      'https://api-v1.mamaearth.in/v1/order/order-list',
      'https://api-v1.mamaearth.in/v1/order/orders'
    ];
    for (var i = 0; i < urls.length; i++) {
      try {
        var response = await fetch(urls[i], { method: 'GET', headers: honasaHeaders() });
        var json = await response.json().catch(function () { return {}; });
        kpLog('[KpAccount] Mamaearth orders API', urls[i], response.status, json);
        if (!response.ok) continue;
        var list =
          (json.data && (json.data.orders || json.data.items || json.data.list)) ||
          json.orders ||
          json.data ||
          json.items ||
          [];
        if (!Array.isArray(list) && list && typeof list === 'object') {
          list = list.edges ? list.edges.map(function (edge) { return edge.node || edge; }) : [];
        }
        if (!Array.isArray(list)) continue;
        var mapped = list.map(mapHonasaOrder).filter(Boolean);
        if (mapped.length) return mapped;
      } catch (e) {
        console.warn('[KpAccount] Mamaearth orders API failed', urls[i], e);
      }
    }
    return [];
  }

  async function fetchCustomer(force) {
    var token = currentAccessToken();
    ordersLoadError = null;
    if (!storefrontToken()) {
      ordersLoadError = 'missing-storefront-token';
      console.warn('[KpAccount] Storefront API token missing — cannot load Shopify firstName/lastName/orders');
      return customerRecord || null;
    }
    if (!token) {
      ordersLoadError = 'missing-customer-token';
      console.warn('[KpAccount] No customerAccessToken (acs_tkn/cap_tkn) — cannot query Storefront customer');
      return customerRecord || null;
    }
    if (customerRecord && !force) return customerRecord;
    var data = await storefrontQuery(CUSTOMER_QUERY, { token: token });
    customerRecord = data && data.customer;
    kpLog('[KpAccount] Storefront customer query result', {
      hasCustomer: !!customerRecord,
      firstName: customerRecord && customerRecord.firstName,
      lastName: customerRecord && customerRecord.lastName,
      email: customerRecord && customerRecord.email,
      orderCount: customerRecord ? ((((customerRecord.orders || {}).edges) || []).length) : 0
    });
    if (customerRecord) {
      ordersCache = (((customerRecord.orders || {}).edges) || []).map(function (edge) { return mapOrder(edge.node); });
      hydrateShopifyCustomer(payloadRecord, customerRecord);
      hydrateDom();
    } else {
      ordersLoadError = 'storefront-customer-null';
    }
    return customerRecord;
  }

  var ordersInFlight = null;

  /**
   * Concurrent callers share one request. Without this, init() and
   * bootAccountPage() each fired their own forced fetch and the orders API was
   * hit twice on every account page load.
   */
  function fetchOrders(force) {
    if (ordersCache && !force) return Promise.resolve(ordersCache);
    if (ordersInFlight) return ordersInFlight;
    ordersInFlight = fetchOrdersUncached(force).then(function (result) {
      ordersInFlight = null;
      return result;
    }, function (error) {
      ordersInFlight = null;
      throw error;
    });
    return ordersInFlight;
  }

  async function fetchOrdersUncached(force) {

    // Kwikpass customer API first; Storefront / Mamaearth order API stay as fallbacks.
    if (kpGraphqlUrl()) {
      try {
        var kpOrders = await fetchKpOrders();
        if (kpOrders) {
          ordersCache = kpOrders;
          ordersLoadError = kpOrders.length ? null : 'empty';
          return ordersCache;
        }
      } catch (error) {
        console.warn('[KpAccount] Kwikpass orders fetch failed, falling back', error);
        ordersLoadError = error.code || 'kp-orders-failed';
      }
    }

    await fetchCustomer(force);
    if (ordersCache && ordersCache.length) return ordersCache;
    var honasaOrders = await fetchHonasaOrders();
    if (honasaOrders.length) {
      ordersCache = honasaOrders;
      ordersLoadError = null;
      return ordersCache;
    }
    return ordersCache || [];
  }

  async function handleKpToken(kpToken) {
    var decoded = await decodeKpToken(kpToken);
    var payload = normalizePayload(decoded);
    kpLog('[KpAccount] kp_data_sent / kpToken received', {
      rawKpToken: redactToken(typeof kpToken === 'string' ? kpToken : JSON.stringify(kpToken)),
      decodedKeys: decoded && typeof decoded === 'object' ? Object.keys(decoded) : [],
      decoded: snapshotObject(decoded, 0)
    });
    if (!payload) return null;
    if (!payload.customerAccessToken) {
      payload.customerAccessToken = readTokenValue('acs_tkn') || readTokenValue('cap_tkn');
    }
    await applyPayload(payload);
    // No order prefetch here — this runs on every page, and only the account
    // surfaces need orders. bootAccountPage() fetches them when relevant.
    await logKpDebug('after handleKpToken', { decoded: decoded, payload: payload, customer: customerRecord });
    window.dispatchEvent(new CustomEvent('kp-customer-ready', { detail: { payload: payload, customer: customerRecord } }));
    resolveReady();
    await bootAccountPage();
    return payload;
  }

  function resolveReady() {
    readyResolvers.forEach(function (resolve) { resolve(window.Shopify && window.Shopify.customer); });
    readyResolvers = [];
  }

  function whenReady() {
    if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
      return Promise.resolve(window.Shopify.customer);
    }
    return new Promise(function (resolve) { readyResolvers.push(resolve); });
  }

  function kpSdk() {
    return window.___KP_LOGIN_SDK_INSTANCE___ || window.__KP_LOGIN_SDK_INSTANCE__;
  }

  function waitUntil(predicate, timeoutMs) {
    return new Promise(function (resolve) {
      if (predicate()) return resolve(true);
      var started = Date.now();
      var timer = setInterval(function () {
        if (predicate() || Date.now() - started >= timeoutMs) {
          clearInterval(timer);
          resolve(!!predicate());
        }
      }, 80);
    });
  }

  async function requestStoredKpToken() {
    await waitUntil(function () {
      return !!(kpSdk() || readTokenValue('kpToken') || currentAccessToken() || kwikSessionToken());
    }, 4000);

    var sdk = kpSdk();
    if (sdk && typeof sdk.getKpToken === 'function') {
      return sdk.getKpToken();
    }
    var stored = readTokenValue('kpToken');
    if (stored) return handleKpToken(stored);
    if (kwikSessionToken() && sdk && typeof sdk.handleKpLogin === 'function') {
      return sdk.handleKpLogin();
    }
    return null;
  }

  function showLoader(root, message) {
    if (!root) return;
    root.innerHTML =
      '<div class="kp-loader" role="status" aria-live="polite">' + escapeHtml(message || 'Loading…') + '</div>' +
      '<div class="kp-skeleton" aria-hidden="true">' +
        '<div class="kp-skeleton-line w-40"></div>' +
        '<div class="kp-skeleton-line w-80"></div>' +
        '<div class="kp-skeleton-line w-60"></div>' +
        '<div class="kp-skeleton-line w-80"></div>' +
      '</div>';
  }

  function showError(root, message, retry) {
    if (!root) return;
    root.innerHTML =
      '<div class="kp-error" role="alert">' + escapeHtml(message) +
      (retry ? '<button type="button" data-kp-retry>Retry</button>' : '') + '</div>';
    var button = root.querySelector('[data-kp-retry]');
    if (button && retry) button.addEventListener('click', retry);
  }

  /**
   * Mirrors main-account.liquid:49-133. Row order, labels and the data-label
   * attributes all matter — the responsive CSS uses data-label to draw the
   * stacked mobile layout, so omitting it breaks the table on phones.
   */
  async function renderProfile(root) {
    if (!root) return;
    var customer = customerRecord;
    var details = kpDetailsRecord;
    var payload = payloadRecord || {};
    var local = await localKpIdentity();

    var address = (details && details.defaultAddress) || (customer && customer.defaultAddress) || {};
    var name = displaySafe(
      (details && [details.firstName, details.lastName].filter(Boolean).join(' ')) ||
      (customer && (customer.displayName || [customer.firstName, customer.lastName].filter(Boolean).join(' '))) ||
      payload.name || address.name || ''
    );
    var email = displaySafe(
      (details && details.email) || (customer && customer.email) || payload.email || local.email || ''
    );
    var phone = displaySafe(
      (details && details.phone) || address.phone || (customer && customer.phone) ||
      payload.phone || local.phone || ''
    );
    // Liquid: customer.name | default: default_address.name | default: customer.email
    var displayName = name || email || phone || 'there';

    var rows = [];
    function row(label, value) {
      if (!value) return;
      rows.push(
        '<tr><td><strong>' + escapeHtml(label) + '</strong></td>' +
        '<td data-label="' + escapeHtml(label) + '">' + escapeHtml(value) + '</td></tr>'
      );
    }

    row('Name', displayName);
    row('E-mail', email);
    if (address && (address.address1 || address.city || address.zip)) {
      row('Address 1', address.address1);
      row('Address 2', address.address2);
      var cityLine = [address.city, address.provinceCode || address.province, address.country]
        .filter(Boolean).join(', ');
      row('Country/region', cityLine);
      row('Postal/ZIP code', address.zip);
      row('Phone', address.phone || phone);
    } else {
      row('Phone', phone);
    }
    if (!rows.length) row('Name', 'there');

    root.innerHTML =
      '<p class="hdt-account-hello">Hello <strong data-kp-customer-name>' + escapeHtml(displayName) +
        '</strong> (not <strong>' + escapeHtml(displayName) + '</strong>? ' +
        '<a data-no-instant href="#" data-kp-logout>Log out</a>)</p>' +
      '<p class="hdt-account-hello-mob">Hello, <span data-kp-customer-name>' +
        escapeHtml(displayName) + '</span></p>' +
      '<div class="profile-nav-mob"></div>' +
      '<div style="margin:2rem 0;" class="hdt-s-text account-heading">' +
        '<p><strong>Account details</strong></p></div>' +
      '<div class="hdt-table-res-df profile-details">' +
        '<table class="hdt-table-infos"><tbody>' + rows.join('') + '</tbody></table>' +
      '</div>';

    // The sidebar nav is desktop-only; clone it for the mobile block.
    var navSource = document.querySelector('.hdt-account-sidebar .hdt-account-nav');
    var navTarget = root.querySelector('.profile-nav-mob');
    if (navSource && navTarget) navTarget.innerHTML = navSource.innerHTML;

    // Deliberately not named `logout` — that would shadow the logout() function
    // this handler needs to call.
    var logoutLink = root.querySelector('[data-kp-logout]');
    if (logoutLink) {
      logoutLink.addEventListener('click', function (event) {
        event.preventDefault();
        logout();
      });
    }
  }

  var STACK_DARK  = 'https://cdn.shopify.com/s/files/1/0888/8057/8807/files/dark.png?v=1762752003';
  var STACK_LIGHT = 'https://cdn.shopify.com/s/files/1/0888/8057/8807/files/light.png?v=1762751999';
  var ARROW_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">' +
    '<path d="M1 1L6 5.81482L1 11" stroke="#505050" stroke-width="1.83255" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /**
   * Branch order copied from main-orderList.liquid:265-370 —
   * Refunded, Cancelled, multi-shipment, Returned, Delivered, then
   * Delayed / Arriving-by. Getting the order wrong shows the wrong state.
   */
  function orderStatusHtml(order) {
    var migrated = order.isMigrated;
    var fin = String(order.financialStatus || '').toUpperCase();

    if (fin === 'REFUNDED' || fin === 'PARTIALLY_REFUNDED') {
      var rrn = order.rrnDetails;
      var amount = 0;
      if (Array.isArray(rrn)) {
        rrn.forEach(function (r) { amount += Number(r && r.amount) || 0; });
      }
      return '<div class="refund-div">' +
             '<p class="arriving-date">Refund Credited</p>' +
             '<p class="refund-text">Your refund' +
             (amount > 0 ? ' of <span>' + formatMoney(amount) + '</span>' : '') +
             ' has been processed successfully.</p></div>';
    }

    if (order.cancelled) {
      var html = '<p class="arriving-date">Cancelled' +
                 (!migrated && order.canceledAt ? ' on ' + escapeHtml(formatDate(order.canceledAt)) : '') +
                 '</p>';
      var reason = String(order.cancelReason || '').replace(/^Other:\s*/, '').trim();
      if (reason) {
        html += '<p class="cancelled-reason-text">Reason: ' +
                '<span class="cancelled-reason">' + escapeHtml(reason) + '</span></p>';
      }
      return html;
    }

    var totalShipments = (order.shipmentCount || 0) + (order.pendingItemsCount > 0 ? 1 : 0);
    if (totalShipments > 0) {
      return partialStatusHtml(order, totalShipments);
    }

    if (order.returnStatus === 'RETURNED') {
      return '<p class="arriving-date">Returned' +
             (!migrated && order.returnDate ? ' on ' + escapeHtml(formatDate(order.returnDate)) : '') + '</p>';
    }

    if (order.deliveryStatus === 'DELIVERED') {
      var out = '<p class="arriving-date">Delivered' +
                (order.deliveredDate ? ' on ' + escapeHtml(formatDate(order.deliveredDate)) : '') + '</p>';
      var cashback = findAttribute(order.attributes || {}, 'CASHBACK');
      if (cashback) {
        // main-orderList.liquid:364 prints the attribute raw: "₹50.5", not "₹50.50".
        out += '<p class="cashback-text">Cashback of <span>₹' + escapeHtml(cashback) +
               '</span> received on this order</p>';
      }
      return out;
    }

    // Liquid falls back to created_at + 5 days when there is no EDD.
    var edd = order.estimatedDeliveryDate;
    var eddTime = edd ? new Date(edd).getTime() : NaN;
    if (Number.isNaN(eddTime)) {
      eddTime = new Date(order.createdAt).getTime() + 432000 * 1000;
    }
    if (Date.now() >= eddTime) {
      return '<p class="arriving-date">Order has been Delayed</p>' +
             '<p class="cancelled-reason-text">Status: <span class="cancelled-reason">' +
             'There’s been a minor logistics delay, but your order will be on its way very soon.' +
             '</span></p>';
    }
    return '<p class="arriving-date">Arriving by ' + escapeHtml(formatDate(new Date(eddTime))) + '</p>';
  }

  /**
   * Port of orderListPartialStatus.liquid. Aggregates every shipment into one
   * headline — "Partially Delivered", "Shipped on 14 Aug 2026" and so on. A
   * plain shipment count would hide the fact that some parcels have arrived.
   */
  function partialStatusHtml(order, totalShipments) {
    var shipments = (order.shipmentDetails && order.shipmentDetails.shipments) || [];
    var count = { delivered: 0, returned: 0, inTransit: 0, outForDelivery: 0 };
    var latest = { delivered: '', returned: '', inTransit: '', outForDelivery: '' };

    function keepLatest(bucket, value) {
      if (!value) return;
      if (!latest[bucket] || new Date(value) > new Date(latest[bucket])) latest[bucket] = value;
    }

    shipments.forEach(function (sh) {
      var st = sh.status;
      if (st === 'DELIVERED') {
        count.delivered++; keepLatest('delivered', sh.deliveryDate);
      } else if (RETURNED_SHIPMENT_STATES.indexOf(st) !== -1) {
        // orderListPartialStatus.liquid:49 counts RETURN_CLOSE and
        // FAILED_TO_DELIVER by shipment status alone — a failed delivery that
        // came back is a return even before its return_status is set.
        count.returned++; keepLatest('returned', sh.returnDate);
      } else if (st === 'OUT_FOR_DELIVERY') {
        count.outForDelivery++; keepLatest('outForDelivery', sh.outForDeliveryDate);
      } else if (st === 'IN_TRANSIT') {
        count.inTransit++; keepLatest('inTransit', sh.inTransitDate);
      }
    });

    function withDate(label, value) {
      var d = value ? formatDate(value) : '';
      return d ? label + ' on ' + d : label;
    }

    // "All" states win over "partial" ones, exactly as the Liquid orders them.
    var status = 'Confirmed';
    if (count.delivered === totalShipments && totalShipments > 0) {
      status = withDate('Delivered', latest.delivered);
    } else if (count.returned === totalShipments && totalShipments > 0) {
      status = withDate('Returned', latest.returned);
    } else if (count.inTransit === totalShipments && totalShipments > 0) {
      status = withDate('Shipped', latest.inTransit);
    } else if (count.outForDelivery === totalShipments && totalShipments > 0) {
      status = withDate('Out for Delivery', latest.outForDelivery);
    } else if (count.returned > 0) {
      status = 'Partially Returned';
    } else if (count.delivered > 0) {
      status = 'Partially Delivered';
    } else if (count.outForDelivery > 0) {
      status = 'Partially Out for Delivery';
    } else if (count.inTransit > 0) {
      status = 'Partially Shipped';
    }

    var settled = status.indexOf('Delivered') === 0 || status.indexOf('Returned') === 0;
    return '<div class="order-multiple-deliveries-card">' +
             '<p class="arriving-date order-multiple-deliveries-title">' + escapeHtml(status) + '</p>' +
             (totalShipments > 1 && !settled
               ? '<p class="order-multiple-deliveries-text">Your order will be delivered in multiple shipments.</p>'
               : '') +
           '</div>';
  }

  function renderOrderList(root) {
    if (!root) return;
    var orders = ordersCache || [];
    if (!orders.length) {
      var message = 'You haven’t placed any orders yet.';
      if (ordersLoadError) message = 'We couldn’t load your orders. Refresh the page or log in again.';
      root.innerHTML =
        '<div class="hdt-form__message hdt-d-flex hdt-items-center hdt-shopify-message">' +
        '<a href="/collections/all">Place an order</a><span>' + escapeHtml(message) + '</span></div>';
      return;
    }

    root.innerHTML = orderCardsHtml(orders) + loadMoreButtonHtml();
    bindLoadMore(root);
  }

  function loadMoreButtonHtml() {
    if (!ordersPageInfo.hasNextPage) return '';
    return '<div class="hdt-pagination-wrapp">' +
             '<button type="button" data-kp-load-more ' +
             'class="hdt-loamore-btn hdt-btn hdt-oh hdt-relative hdt-btn-solid ' +
             'hdt-inline-flex hdt-align-center hdt-justify-center"><span>Load more orders</span></button>' +
           '</div>';
  }

  /**
   * Appends the next page in place rather than re-rendering the list, so cards
   * already on screen do not flicker and scroll position is preserved.
   */
  function bindLoadMore(root) {
    var wrap = root.querySelector('[data-kp-load-more]');
    if (!wrap) return;
    wrap.addEventListener('click', function () {
      var label = wrap.querySelector('span') || wrap;
      var original = label.textContent;
      wrap.disabled = true;
      label.textContent = 'Loading…';
      loadMoreOrders()
        .then(function (added) {
          var container = wrap.parentElement;
          if (container && container.parentElement) container.parentElement.removeChild(container);
          if (added.length) root.insertAdjacentHTML('beforeend', orderCardsHtml(added));
          if (ordersPageInfo.hasNextPage) {
            root.insertAdjacentHTML('beforeend', loadMoreButtonHtml());
            bindLoadMore(root);
          }
        })
        .catch(function (error) {
          console.warn('[KpAccount] load more failed', error);
          wrap.disabled = false;
          label.textContent = original || 'Load more orders';
        });
    });
  }

  function orderCardsHtml(orders) {
    return orders.map(function (order) {
      var first = order.lineItems[0] || {};
      var many = order.itemCount > 1;
      var img = first.image || DEFAULT_ORDER_IMAGE;
      var href = '/pages/order?id=' + encodeURIComponent(order.id);

      return (
        '<a href="' + escapeHtml(href) + '" class="order-listing-link" ' +
        'aria-label="Order ' + escapeHtml(order.name) + '">' +
          '<div class="order-table' + (many ? ' extra' : '') + '">' +
            '<div class="order-img">' +
              '<img src="' + escapeHtml(img) + '" alt="" width="200" height="200" loading="lazy" ' +
              'sizes="(max-width: 740px) 80px, 92px" class="line-item__image">' +
              (many
                ? '<img src="' + STACK_DARK + '" alt="" width="20" height="20" loading="lazy" class="dark">' +
                  '<img src="' + STACK_LIGHT + '" alt="" width="20" height="20" loading="lazy" class="light">'
                : '') +
            '</div>' +
            '<div class="order-detail">' +
              '<div class="details">' +
                '<p class="item-count">' + escapeHtml(order.itemCount) +
                  ' Item' + (many ? 's' : '') + '</p>' +
                '<p class="order-id">Order ID : <span>' + escapeHtml(order.name) + '</span></p>' +
                orderStatusHtml(order) +
              '</div>' +
              '<div class="arrow-div">' + ARROW_ICON + '</div>' +
            '</div>' +
          '</div>' +
        '</a>'
      );
    }).join('');
  }

  // =====================================================================
  //  Order detail UI
  //  Ports the state machine from sections/main-order.liquid and
  //  snippets/orderProgressBar.liquid, and emits the theme's own class
  //  names so hdt-customer.css styles it without new CSS.
  // =====================================================================

  /** Liquid renders dates as "Aug 14, 2026"; pass those through untouched. */
  function pbDate(value) {
    if (!value) return '';
    var parsed = formatDate(value);
    return parsed || String(value);
  }

  /**
   * Mirrors the per-shipment flags computed in main-order.liquid:770-870.
   * `shipment` is a normalizeShipmentDetails() entry, or null for an
   * order-level (single, unshipped) view.
   */
  function shipmentState(order, shipment) {
    var s = shipment || {};
    var status = String(s.status || '').toUpperCase();
    // Per shipment only (main-order.liquid:2335). The order-level return_status
    // flips to RETURNED as soon as ANY parcel comes back, so falling back to it
    // marked every sibling shipment as returned. Only the no-shipments fallback
    // (shipment === null) is order-level, and there the order flag is correct.
    var returnStatus = String((shipment ? s.returnStatus : order.returnStatus) || '').toUpperCase();

    var isCancelled = !!order.cancelled;
    var isReturned =
      RETURNED_SHIPMENT_STATES.indexOf(status) !== -1 ||
      RETURN_STATES.indexOf(returnStatus) !== -1;
    var isDelivered = status === 'DELIVERED';
    var isOutForDelivery = status === 'OUT_FOR_DELIVERY';
    var isShipped = !!(s.fulfilledItems && s.fulfilledItems.length) || isDelivered || isOutForDelivery;

    // Delay only applies while the parcel is still in flight.
    var isDelayed = false;
    var expected = s.expectedDeliveryDate || order.estimatedDeliveryDate;
    if (!isCancelled && !isReturned && !isDelivered) {
      var due = expected ? new Date(expected).getTime() : NaN;
      if (!Number.isNaN(due) && Date.now() >= due) isDelayed = true;
    }

    var totalSteps = 4;
    if (isCancelled) totalSteps = 2;
    else if (isReturned) totalSteps = 3;

    // Terminal states fill the bar: couriers often skip OUT_FOR_DELIVERY, and a
    // delivered parcel showing 3-of-4 steps reads as still in transit.
    var completed = 1;
    if (isCancelled || isReturned || isDelivered) {
      completed = totalSteps;
    } else {
      if (isShipped) completed += 1;
      if (isOutForDelivery) completed += 1;
    }

    return {
      isCancelled: isCancelled,
      isReturned: isReturned,
      isDelivered: isDelivered,
      isOutForDelivery: isOutForDelivery,
      isShipped: isShipped,
      isCod: String(order.paymentMethod || '').toUpperCase() === 'COD',
      isMigrated: !!order.isMigrated,
      isDelayed: isDelayed,
      confirmedDate: pbDate(order.createdAt),
      shippedDate: pbDate(s.inTransitDate || s.fulfillmentCreatedAt),
      outForDeliveryDate: pbDate(s.outForDeliveryDate || order.outForDeliveryDate),
      deliveredDate: pbDate(s.deliveryDate || order.deliveredDate),
      returnDate: pbDate(s.returnDate || order.returnDate),
      cancelledDate: pbDate(order.canceledAt),
      cancellationReason: order.cancelReason || '',
      failedDeliveryReason: s.failedDeliveryReason || '',
      expectedDeliveryDate: pbDate(expected),
      trackingNumber: s.trackingNumber || '',
      trackingUrl: s.trackingUrl || '',
      latestTrackingNote: s.latestTrackingNote || '',
      invoiceUrl: s.invoiceUrl || order.invoiceUrl || '',
      // show_shipment_tabs, order.total_price and order.id, as main-order.liquid
      // passes them to orderProgressBar.
      showShipmentTabs: orderDisplayStatus(order).partial,
      orderTotal: order.total,
      orderId: numericId(order.id),
      totalSteps: totalSteps,
      completedSteps: completed,
      percentage: totalSteps > 1 ? Math.round(((completed - 1) / (totalSteps - 1)) * 100) : 0
    };
  }

  /** One milestone row. `state` is 'done' | 'current' | 'todo'. */
  var COD_TAG = '<div class="cod-order-status-tag">COD</div>';

  function milestone(label, state, dateText, extraHtml, lineCompleted, opts) {
    opts = opts || {};
    var done = state === 'done' || state === 'current';
    // Marker colours per orderProgressBar.liquid. Note .bg-black is #0066CC
    // (blue) in order-progress-styles.liquid — the class names are historical,
    // so map by state name, not by the colour the name implies.
    var tone = ' active bg-' + (opts.tone || 'black') + ' border-' + (opts.tone || 'black');
    // Inactive colour differs per step in orderProgressBar.liquid: Shipped uses
    // .color-grey (#d9d9d9), Out for Delivery / Delivered use .color-gray (#727272).
    var labelHtml = '<div class="orderStatusLabel ' +
      (done ? 'color-black text-bold' : (opts.todoClass || 'color-grey')) + '">' +
      escapeHtml(label) + '</div>';
    // Active labels sit in .label-with-tag so a COD tag can ride beside them;
    // inactive ones are bare, as in the Liquid.
    if (done) labelHtml = '<div class="label-with-tag">' + labelHtml + (opts.tag || '') + '</div>';
    return (
      '<div class="pt-status-milestone' + (lineCompleted ? ' line-completed' : '') + '"' +
        (opts.status ? ' data-status="' + opts.status + '"' : '') + '>' +
        '<div class="pt-status-milestone-marker' + (done ? tone : '') + '">' +
          '<div class="pt-status-milestone-marker-check' + (done ? ' active' : '') + '"></div>' +
        '</div>' +
        '<div class="pt-status-milestone-label" role="img" aria-label="' + escapeHtml(label + ': ' + label) + '">' +
          labelHtml +
          (dateText ? '<div class="' + (opts.dateClass || 'orderStatusDate') + '">' + escapeHtml(dateText) + '</div>' : '') +
          (extraHtml || '') +
        '</div>' +
      '</div>'
    );
  }

  /** The "Status: …" line: Delayed wins, else the latest tracking note. */
  function statusDetailHtml(st) {
    var body = '';
    if (st.isDelayed) {
      body = 'Delayed,' + (st.latestTrackingNote
        ? ' ' + escapeHtml(st.latestTrackingNote)
        : ' but just slightly — your order has been shipped from final delivery facility.');
    } else if (st.latestTrackingNote) {
      body = escapeHtml(st.latestTrackingNote);
    }
    return body
      ? '<div class="shipment-status-detail"><span class="status-label">Status:</span> <span>' + body + '</span></div>'
      : '';
  }

  function statusNote(st) {
    var detail = statusDetailHtml(st);
    return detail ? '<div class="shipment-status-box">' + detail + '</div>' : '';
  }

  /** The four/three/two step tracker. */
  function progressBarHtml(st) {
    var html = '<div class="tracking_sections"><p class="track-order-text">Track order</p>' +
               '<div class="tracking_confirm"><div class="pt-status-milestones" ' +
               'style="--completed-percentage: ' + st.percentage + '%;">';

    var confirmedActive = !st.isShipped && !st.isCancelled && !st.isReturned;
    html += milestone(
      'Confirmed', 'done', st.confirmedDate,
      confirmedActive ? statusNote(st) : '',
      st.isShipped || st.isCancelled,
      { tag: st.isCod && confirmedActive ? COD_TAG : '' }
    );

    if (st.isCancelled) {
      html += milestone('Cancelled', 'done',
        st.cancelledDate ? 'Order cancelled on ' + st.cancelledDate : '',
        st.cancellationReason
          ? '<span class="orderStatusDate cancel-reason-line" style="margin-top: 4px;">' +
            '<span class="reason-text"><b>Reason: </b></span>' +
            '<span class="cancel-reason-value">' + escapeHtml(st.cancellationReason) + '</span></span>'
          : '', false, { tone: 'red', status: 'cancelled' });
    } else {
      // orderProgressBar.liquid:136-163. Once past Shipped (delivered/returned)
      // only the date shows; otherwise one box holds the status line and the AWB,
      // followed by "See all updates". COD orders split across shipments drop the
      // status line — the AWB stays.
      var shippedExtra = '';
      if (st.isShipped && !st.isDelivered && !st.isReturned) {
        var showDetail = st.isCod && st.showShipmentTabs ? false : !!(st.isDelayed || st.latestTrackingNote);
        var box = (showDetail && !st.isOutForDelivery ? statusDetailHtml(st) : '') +
          (st.trackingNumber
            ? '<div class="shipment-awb"><span class="awb-label">AWB:</span> <span class="awb-number">' +
              escapeHtml(st.trackingNumber) + '</span></div>'
            : '');
        if (box) shippedExtra += '<div class="shipment-status-box">' + box + '</div>';
        if (st.trackingNumber) {
          // Opens order-tracking-update's popup; bindOrderDetail points it at
          // this shipment first, since the popup reads from a single section.
          shippedExtra += '<a href="javascript:void(0);" class="tracking-url orderStatusDate js-open-delivery-popup"' +
            ' data-tracking-number="' + escapeHtml(st.trackingNumber) + '"' +
            ' data-order-id="' + escapeHtml(st.orderId || '') + '"' +
            ' data-edd="' + escapeHtml(st.expectedDeliveryDate || '') + '">See all updates</a>';
        }
      }
      html += milestone('Shipped', st.isShipped ? 'done' : 'todo',
        st.isShipped && (st.isDelivered || st.isReturned) && !st.isMigrated ? st.shippedDate : '',
        shippedExtra, st.isOutForDelivery || st.isDelivered || st.isReturned,
        { status: 'shipped', todoClass: 'color-grey',
          tag: st.isCod && !st.isOutForDelivery && !st.isDelivered && !st.isReturned ? COD_TAG : '' });

      if (st.isReturned) {
        // orderProgressBar.liquid:184-203 has two distinct shapes here:
        // with a failure reason -> "Returned on {date}" plus a Reason line;
        // without one -> an "Undelivered on {date}" box with a View Reason link.
        var returnDateValid = st.returnDate && String(st.returnDate).toUpperCase() !== 'NULL';
        var returnedDateText = '';
        var returnedExtra = '';
        if (!st.isMigrated) {
          if (st.failedDeliveryReason) {
            if (returnDateValid) returnedDateText = 'Returned on ' + st.returnDate;
            returnedExtra =
              '<span class="orderStatusDate cancel-reason-line" style="margin-top: 4px;">' +
                '<span class="reason-text">Reason: </span>' +
                '<span class="cancel-reason-value">' + escapeHtml(st.failedDeliveryReason) + '</span>' +
              '</span>';
          } else if (returnDateValid) {
            returnedExtra =
              '<div class="return-date-container"><div class="undelivered-status-box">' +
                '<span class="undelivered-status-text"><strong>Status:</strong> Undelivered on ' +
                  escapeHtml(st.returnDate) + '</span>' +
                '<a href="javascript:void(0);" class="view-reason-link js-open-undelivered-popup">View Reason</a>' +
              '</div></div>';
          }
        }
        html += milestone('Returned', 'done', returnedDateText, returnedExtra, false,
          { tone: 'yellow', status: 'returned' });
      } else {
        // orderProgressBar.liquid:208-262. The active step shows its date and
        // a COD tag; its status line is commented out in the Liquid. The
        // cash-on-delivery reminder only appears when there are no tabs.
        var ofdActive = st.isOutForDelivery || st.isDelivered;
        var ofdDate = ofdActive && st.outForDeliveryDate &&
          String(st.outForDeliveryDate).toUpperCase() !== 'NULL' ? st.outForDeliveryDate : '';
        var ofdExtra = st.isCod && st.isOutForDelivery && !st.isDelivered && !st.showShipmentTabs
          ? '<div class="cod-order-status-text">Your order is cash on delivery. Please keep cash/online amount of ' +
            escapeHtml(formatMoneyExact(st.orderTotal)) + ' ready with you for the delivery person.</div>'
          : '';
        html += milestone('Out for Delivery', ofdActive ? 'done' : 'todo', ofdDate, ofdExtra, st.isDelivered,
          { tone: 'blue', status: 'out-for-delivery', todoClass: 'color-gray',
            tag: st.isCod && st.isOutForDelivery && !st.isDelivered ? COD_TAG : '' });

        // orderProgressBar.liquid:265-300. Done: date plus invoice link. Pending:
        // "Estimated delivery by …" in the teal .arriving-date style.
        var deliveredDate = '';
        var deliveredExtra = '';
        if (!st.isMigrated) {
          if (st.isDelivered) {
            deliveredDate = st.deliveredDate;
            if (st.deliveredDate && st.invoiceUrl) {
              deliveredExtra = '<a href="' + escapeHtml(st.invoiceUrl) + '" target="_blank" ' +
                'class="download-invoice tracking-url">Download your invoice</a>';
            }
          } else if (st.expectedDeliveryDate) {
            deliveredDate = 'Estimated delivery by ' + st.expectedDeliveryDate;
          }
        }
        html += milestone('Delivered', st.isDelivered ? 'done' : 'todo', deliveredDate, deliveredExtra, false,
          { status: 'delivered', todoClass: 'color-gray',
            dateClass: st.isDelivered ? '' : 'orderStatusDate arriving-date' });
      }
    }

    html += '</div></div></div>';
    return html;
  }

  var CART_ICON = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"> <path d=\"M8.60327 4.3418H6.33093C5.7437 4.3418 5.15647 4.57158 4.72242 4.98009C4.28838 5.38861 4.05859 5.95031 4.05859 6.53754V19.7631C4.05859 20.3503 4.28838 20.912 4.72242 21.3205C5.15647 21.729 5.71817 21.9588 6.33093 21.9588H17.6416C18.2288 21.9588 18.816 21.729 19.2501 21.3205C19.6841 20.912 19.9139 20.3503 19.9139 19.7631V6.53754C19.9139 5.95031 19.6841 5.38861 19.2501 4.98009C18.816 4.57158 18.2543 4.3418 17.6416 4.3418H15.3692\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> <path d=\"M13.0204 2.04297H10.8501C9.65014 2.04297 8.67993 3.03871 8.67993 4.26425C8.67993 5.48978 9.65014 6.48552 10.8501 6.48552H13.0204C14.2204 6.48552 15.1906 5.48978 15.1906 4.26425C15.2161 3.03871 14.2459 2.04297 13.0204 2.04297Z\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> <path d=\"M12 13H16\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> <path d=\"M12 17H16\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> <path d=\"M8 13H9\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> <path d=\"M8 17H9\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> </svg>";
  var PRICE_ICON = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"> <path d=\"M6.2931 2C5.70051 2 5.10791 2.26087 4.66991 2.72464C4.23191 3.18841 4.00002 3.82609 4.00002 4.49275V19.5072C4.00002 20.1739 4.23191 20.8116 4.66991 21.2754C5.10791 21.7391 5.67474 22 6.2931 22H17.7069C18.2995 22 18.8921 21.7391 19.3301 21.2754C19.7681 20.8116 20 20.1739 20 19.5072V4.49275C20 3.82609 19.7681 3.18841 19.3301 2.72464C18.8921 2.26087 18.3253 2 17.7069 2H6.2931Z\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\" /> <path d=\"M8 15H16\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> <path d=\"M8 18H13\" stroke=\"#1D1D1D\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> <path d=\"M10 5H14\" stroke=\"#1D1D1D\" stroke-width=\"1.2\" stroke-linecap=\"round\"/> <path d=\"M10 7H14\" stroke=\"#1D1D1D\" stroke-width=\"1.2\" stroke-linecap=\"round\"/> <path d=\"M10.4048 5.00514C11.3223 4.95133 13.1033 5.30107 12.9953 6.96908C12.8874 8.63708 10.9445 8.87921 10 8.7985L12.9953 12\" stroke=\"#1D1D1D\" stroke-width=\"1.2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" /> </svg>";

  /** Matches the production markup in main-order.liquid:2501-2549. */
  function lineItemsHtml(order, ids, qtyById) {
    var items = order.lineItems || [];
    if (ids) items = items.filter(function (i) { return ids.indexOf(String(i.id)) !== -1; });
    if (!items.length) return '';

    var rows = items.map(function (item) {
      var qty = (qtyById && qtyById[String(item.id)]);
      if (qty == null) qty = item.quantity;
      var img = item.image || DEFAULT_ORDER_IMAGE;
      var isFree = Number(item.total) === 0;
      var priceBlock = isFree
        ? '<p class="price free-price">FREE</p>'
        : '<p class="price"><span class="price-text">Amount :&nbsp;</span>' + formatMoneyExact(item.total) + '</p>' +
          (qty != null ? '<p class="qty-text">Qty : <span>' + escapeHtml(qty) + '</span></p>' : '');

      // Liquid wraps the image in .line-item__image-wrapper and the title in a
      // bare <a> when line_item.url exists, and falls back to <p> when it does
      // not. Mirror both branches.
      var url = item.productUrl;
      var imgTag =
        '<img src="' + escapeHtml(img) + '" alt="" width="200" height="200" loading="lazy" ' +
        'sizes="(max-width: 740px) 80px, 92px" class="line-item__image">';
      var imgHtml = url
        ? '<a href="' + escapeHtml(url) + '" class="line-item__image-wrapper">' + imgTag + '</a>'
        : imgTag;
      var titleHtml = url
        ? '<a href="' + escapeHtml(url) + '">' + escapeHtml(item.title) + '</a>'
        : '<p class="line-item-title">' + escapeHtml(item.title) + '</p>';

      return (
        '<div class="line-item-details">' +
          '<div class="line-item-img">' + imgHtml + '</div>' +
          '<div class="item-detail">' +
            titleHtml +
            '<div class="price-qty-div">' + priceBlock + '</div>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    return (
      '<div class="cartLine-items-wrapper order-shipment-cart-items">' +
        '<div class="items-wrapper-header">' + CART_ICON +
          ' Cart item' + (items.length > 1 ? 's' : '') + ' (' + items.length + ')' +
        '</div>' +
        '<div class="line-items-wrapper">' + rows + '</div>' +
      '</div>'
    );
  }

  function sectionBorder() {
    return '<div class="section-border"><div class="inner-border-top"></div>' +
           '<div class="inner-border-bottom"></div></div>';
  }

  var TRUCK_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">' +
    '<path d="M9.2069 16.4545C9.2069 17.8604 8.09533 19 6.72414 19C5.35295 19 4.24138 17.8604 4.24138 16.4545M9.2069 16.4545C9.2069 15.0487 8.09533 13.9091 6.72414 13.9091C5.35295 13.9091 4.24138 15.0487 4.24138 16.4545M9.2069 16.4545H14.7931M4.24138 16.4545H3V6C3 5.44771 3.44772 5 4 5H14.7931V16.4545M20.3793 16.4545C20.3793 17.8604 19.2677 19 17.8966 19C16.5254 19 15.4138 17.8604 15.4138 16.4545M20.3793 16.4545C20.3793 15.0487 19.2677 13.9091 17.8966 13.9091C16.5254 13.9091 15.4138 15.0487 15.4138 16.4545M20.3793 16.4545H21V11.3636L17.8966 8.18182H14.7931V16.4545M15.4138 16.4545H14.7931" stroke="black" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function addressHtml(order) {
    var a = order.shippingAddress || {};
    // Shopify's `formatted` lines follow the country's address format, which is
    // what Liquid's format_address prints (e.g. "122003 GURGAON HR" for India).
    var postal = (a.formatted && a.formatted.length)
      ? a.formatted.slice()
      : [
          a.company,
          a.address1,
          a.address2,
          [a.city, a.provinceCode || a.province, a.zip].filter(Boolean).join(', '),
          a.country
        ];
    if (a.name && postal[0] !== a.name) postal.unshift(a.name);
    var lines = postal.filter(Boolean);
    // The name falls back to the account holder, so a name alone does not mean we
    // have an address. Require a real postal line before claiming "Shipped to".
    var hasPostal = !!(a.address1 || a.address2 || a.city || a.zip);
    if (!hasPostal) return '';
    // format_address wraps the lines in a <p>; main-order.liquid sizes the text
    // through `.address_subtotal p`, so bare text here renders smaller.
    return (
      '<div class="shiping__address"><div>' +
        '<div class="shipping-header">' + TRUCK_ICON + '<p class="shipped-text">Shipped to</p></div>' +
        '<div class="shipped-address">' +
          '<p>' + lines.map(function (l) { return escapeHtml(l); }).join('<br>') + '</p>' +
          (order.phone
            ? '<span><span>Mob -</span> ' + escapeHtml(order.phone) + '</span>'
            : '') +
        '</div>' +
      '</div></div>'
    );
  }

  /**
   * main-order.liquid keeps the address, price summary and footer CTAs inside
   * one .address_subtotal grid; its 8px gap is the spacing between them.
   */
  function addressSubtotalHtml(order) {
    var address = addressHtml(order);
    return '<div class="address_subtotal">' +
      (address ? address + sectionBorder() : '') +
      priceSummaryHtml(order) +
      ctaHtml(order) +
    '</div>';
  }

  /**
   * Matches main-order.liquid:3290-3399. Rows whose data the schema does not
   * expose yet (subtotal, shipping, savings) are simply omitted rather than
   * faked — Payment Method and Total Paid always render.
   */
  function priceSummaryHtml(order) {
    // Amounts arrive as strings, so "0.0" is truthy — compare numerically or the
    // summary sprouts "Savings -₹0" and a phantom "Refunded ₹0" row.
    function amt(v) {
      var n = Number(v);
      return Number.isFinite(n) ? n : 0;
    }
    function has(v) { return v !== '' && v != null && amt(v) > 0; }

    var rows = '';
    // Liquid uses order.line_items_subtotal_price — the pre-discount sum of the
    // line items. The API's subtotalPrice is already net of discounts, so using
    // it makes the summary look like the discount was applied twice.
    var lineSum = (order.lineItems || []).reduce(function (sum, i) {
      return sum + (Number(i.total) || 0);
    }, 0);
    var orderTotalRow = lineSum > 0 ? lineSum : order.subtotal;
    if (has(orderTotalRow)) {
      rows += '<div class="breakdown">Order Total <span>' + formatMoneyExact(orderTotalRow) + '</span></div>';
    }
    // main-order.liquid compares the ORDER TOTAL against the free-shipping
    // threshold — not the shipping charge — and shows the waived fee struck
    // through. Amounts here are decimal rupees, so compare in rupees.
    var cfg = config();
    var feeRupees = Number(cfg.shippingFee) || 40;
    var thresholdRupees = Number(cfg.freeShippingThreshold) || 399;
    var isFreeShipping = amt(order.total) > thresholdRupees;
    rows += '<div class="breakdown">' +
              '<div class="shipping-label">Shipping' + SHIPPING_TOOLTIP(feeRupees, thresholdRupees) + '</div>' +
              '<span class="shipping" data-total="' + escapeHtml(order.total) + '">' +
                (isFreeShipping
                  ? '<span class="green">FREE</span><span class="slashed">\u20B9' + Math.round(feeRupees) + '</span>'
                  : formatMoneyExact(order.shipping || 0)) +
              '</span></div>';
    // main-order.liquid:3400-3424. MamaCash is part of total_discounts, so the
    // Savings row shows the discount with it removed — otherwise the wallet
    // credit is counted twice. Row order is Savings -> Payment Method -> Mamacash.
    var mamacash = amt(order.mamacashUsed);
    var withoutMamacash = amt(order.discounts) - mamacash;
    if (withoutMamacash > 0) {
      rows += '<div class="breakdown">Savings <span class="green">-' +
              formatMoneyExact(withoutMamacash) + '</span></div>';
    }
    if (order.paymentMethod) {
      rows += '<div class="breakdown">Payment Method <span class="green">' +
              escapeHtml(order.paymentMethod) + '</span></div>';
    }
    if (mamacash !== 0) {
      // Liquid renders "- {{ ... }}" with a space after the minus.
      rows += '<div class="breakdown">Mamacash <span class="green">- ' +
              formatMoneyExact(mamacash) + '</span></div>';
    }
    // Refund figures live in their own card (refundDetailsHtml), matching Liquid.

    return (
      '<div class="subtotal_details">' +
        '<div class="price_details">' + PRICE_ICON + ' Price Summary</div>' +
        '<div class="price-breakdown">' +
          '<div class="breakdown-div">' + rows + '</div>' +
          '<div class="border-div"></div>' +
          '<div class="total-price-div">Total Paid <span>' + formatMoneyExact(order.total) + '</span></div>' +
        '</div>' +
      '</div>'
    );
  }

  /**
   * Exact markup from orderStatusMessage.liquid:102 — one <p class="customer-text">
   * with the name as direct text and the status in a nested <span>. Do not use
   * .hdt-account-hello here: hdt-customer.css hides it below 767px.
   */
  /**
   * Port of orderStatusMessage.liquid + its inputs (main-order.liquid:1666-1695,
   * 2213-2225). An order is shipment-level once shipment_details has shipments or
   * pending items; its headline then comes from counting every parcel's own
   * status, with pending items counted as one more parcel. Only order-level
   * orders use the order-wide return/delivery metafields.
   */
  var CASHBACK_COINS_ICON = 'https://cdn.shopify.com/s/files/1/0888/8057/8807/files/cb_coins.png?v=1772103544';

  /**
   * main-order.liquid:2253-2282. Hidden once the order is refunded, cancelled
   * or its return_status metafield says RETURNED. The value prints raw after
   * the rupee sign, as the Liquid does. main-order.liquid's own CSS styles it
   * as a pill at every width; the coins icon only appears from 874px up.
   */
  function cashbackHtml(order) {
    if (!order.cashback || order.cancelled) return '';
    if (String(order.financialStatus || '').toUpperCase() === 'REFUNDED') return '';
    if (order.returnStatusMeta === 'RETURNED') return '';
    return '<div class="cashback-div">' +
             '<span class="cashback-icon desktop-view"><img src="' + CASHBACK_COINS_ICON + '" alt="cashback-icon" width="50" height="50"></span>' +
             ' Get a cashback of <span>₹' + escapeHtml(order.cashback) + '</span> after delivery' +
           '</div>';
  }

  function orderDisplayStatus(order) {
    var sd = order.shipmentDetails || {};
    var shipments = sd.shipments || [];
    var shipmentCount = order.shipmentCount || shipments.length || 0;
    var pendingCount = order.pendingItemsCount || (sd.pendingItems || []).length || 0;
    var hasShipments = shipments.length > 0 && shipmentCount > 0;
    var shipmentLevel = hasShipments || pendingCount > 0;
    var tabCount = (hasShipments ? shipmentCount : 0) + (pendingCount > 0 ? 1 : 0);

    var returnStatus = String(order.returnStatus || '').toUpperCase();
    var deliveryStatus = String(order.deliveryStatus || '').toUpperCase();

    var status = 'Confirmed';
    if (!shipmentLevel) {
      if (returnStatus === 'RETURNED') status = 'Returned';
      else if (returnStatus === 'IN_PROGRESS') status = 'Processing';
      else if (deliveryStatus === 'DELIVERED') status = 'Delivered';
      else if (deliveryStatus === 'OUT_FOR_DELIVERY') status = 'Out for Delivery';
      else if (String(order.fulfillmentStatus || '').toUpperCase() === 'FULFILLED') status = 'Shipped';
    } else if (hasShipments) {
      var total = shipmentCount + (pendingCount > 0 ? 1 : 0);
      var n = { delivered: 0, returned: 0, inTransit: 0, outForDelivery: 0 };
      shipments.forEach(function (sh) {
        var st = String(sh.status || '').toUpperCase();
        if (st === 'DELIVERED') n.delivered++;
        if (RETURNED_SHIPMENT_STATES.indexOf(st) !== -1) n.returned++;
        if (st === 'IN_TRANSIT') n.inTransit++;
        if (st === 'OUT_FOR_DELIVERY') n.outForDelivery++;
      });
      if (n.delivered === total) status = 'Delivered';
      else if (n.returned === total) status = 'Returned';
      else if (n.inTransit === total) status = 'Shipped';
      else if (n.outForDelivery === total) status = 'Out for Delivery';
      else if (n.returned > 0) status = 'Partially Returned';
      else if (n.delivered > 0) status = 'Partially Delivered';
      else if (n.outForDelivery > 0) status = 'Partially Out for Delivery';
      else if (n.inTransit > 0) status = 'Partially Shipped';
    }
    // Pending-only orders fall through to 'Confirmed', as in the Liquid.

    var isDelayed = false;
    var due = order.estimatedDeliveryDate ? new Date(order.estimatedDeliveryDate).getTime() : NaN;
    if (!Number.isNaN(due) && !order.cancelled && deliveryStatus !== 'DELIVERED' &&
        returnStatus !== 'RETURNED' && returnStatus !== 'IN_PROGRESS' && Date.now() >= due) {
      isDelayed = true;
    }

    return {
      status: status,
      isDelayed: isDelayed,
      shipmentLevel: shipmentLevel,
      partial: shipmentLevel && tabCount > 1
    };
  }

  function orderStatusHeadingHtml(order) {
    var addr = order.shippingAddress || {};
    var who = displaySafe(
      addr.name ||
      (customerRecord && (customerRecord.displayName ||
        [customerRecord.firstName, customerRecord.lastName].filter(Boolean).join(' '))) ||
      (kpDetailsRecord && [kpDetailsRecord.firstName, kpDetailsRecord.lastName].filter(Boolean).join(' ')) ||
      (payloadRecord && payloadRecord.name) || ''
    );

    var hs = orderDisplayStatus(order);
    var text = order.cancelled ? 'Cancelled'
             : (hs.isDelayed && !hs.shipmentLevel) ? 'Delayed'
             : hs.status;
    var delayedClass = hs.isDelayed && !hs.shipmentLevel;
    // .order-detail-mob supplies padding: 0 16px, flex column, gap 16px and
    // centering on mobile. Without it the greeting sits flush to the edge.
    return (
      '<div class="order-detail-mob">' +
      '<p class="customer-text">' +
        (who ? 'Hi ' + escapeHtml(who) + '!' : '') +
        '<span' + (delayedClass ? ' class="order-delayed-text"' : '') +
          ' data-initial-delayed="' + (hs.isDelayed ? 'true' : 'false') + '"' +
          ' data-use-partial-statuses="' + (hs.partial ? 'true' : 'false') + '">' +
          'Your order is ' + escapeHtml(text) +
        '</span>' +
      '</p>' +
      '</div>'
    );
  }

  /**
   * Footer, ported from main-order.liquid:3462-3494. "View Return Policy" always
   * renders; "Cancel Order" only when nothing has shipped yet. Buy Again is NOT
   * a footer button in the Liquid — it is its own section (see buyAgainHtml).
   */
  var RETURN_WINDOW_MS = 604800 * 1000; // 7 days, main-order.liquid:1776
  var SHOP_UTC_OFFSET_MS = 330 * 60 * 1000; // IST — Liquid's date filter renders in shop time
  var SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /**
   * Metafield dates arrive either as ISO strings or as "YYYY-MM-DD HH:MM:SS"
   * with no zone. Liquid reads the latter in shop time, and Safari cannot parse
   * the space-separated form at all, so normalise before handing it to Date.
   */
  function parseShopDate(value) {
    if (!value) return NaN;
    var text = String(value).trim();
    var m = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)$/);
    if (m) text = m[1] + 'T' + m[2] + '+05:30';
    else if (/^\d{4}-\d{2}-\d{2}$/.test(text)) text += 'T00:00:00+05:30';
    return new Date(text).getTime();
  }

  /** Liquid's '%-d %b %Y' in shop time, e.g. "18 Sep 2026". */
  function formatReturnWindowDate(ms) {
    var d = new Date(ms + SHOP_UTC_OFFSET_MS);
    return d.getUTCDate() + ' ' + SHORT_MONTHS[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
  }

  /**
   * Port of main-order.liquid:1717-1777. Shipment orders qualify only once every
   * shipment is DELIVERED or RETURN_CLOSE (FAILED_TO_DELIVER does not count) and
   * nothing is pending; the window runs from the latest delivery. Order-level
   * orders use the order's own delivery status and date.
   */
  function returnExchangeState(order, now) {
    if (order.cancelled) return null;
    var sd = order.shipmentDetails || {};
    var shipments = sd.shipments || [];
    var metaReturned = order.returnStatusMeta === 'RETURNED';
    var deliveredAt = '';

    if (shipments.length && (order.shipmentCount || shipments.length) > 0) {
      var allFinal = shipments.every(function (sh) {
        var st = String(sh.status || '').toUpperCase();
        return st === 'DELIVERED' || st === 'RETURN_CLOSE';
      });
      var pendingOk = !(order.pendingItemsCount > 0);
      var latest = NaN;
      shipments.forEach(function (sh) {
        if (String(sh.status || '').toUpperCase() !== 'DELIVERED' || !sh.deliveryDate) return;
        var t = parseShopDate(sh.deliveryDate);
        if (!Number.isNaN(t) && (Number.isNaN(latest) || t > latest)) { latest = t; deliveredAt = sh.deliveryDate; }
      });
      if (!(allFinal && pendingOk && deliveredAt && !metaReturned)) return null;
    } else {
      deliveredAt = order.deliveredDate;
      if (!(order.deliveryStatus === 'DELIVERED' && deliveredAt && !metaReturned)) return null;
    }

    // Liquid would render an unparseable date as a 1970 "closed" window; show
    // nothing rather than a nonsense date.
    var deliveredMs = parseShopDate(deliveredAt);
    if (Number.isNaN(deliveredMs)) return null;
    var windowEnd = deliveredMs + RETURN_WINDOW_MS;
    return {
      eligible: (now == null ? Date.now() : now) < windowEnd,
      windowEndLabel: formatReturnWindowDate(windowEnd)
    };
  }

  /** main-order.liquid:3441-3468 — the mobile Return / Exchange box. */
  function returnExchangeHtml(order) {
    var state = returnExchangeState(order);
    if (!state) return '';
    var attrs = ' data-order="' + escapeHtml(order.name || '') + '"' +
                ' data-customer="' + escapeHtml(returnExchangeEmail()) + '"';
    return '<div class="return-exchange-box return-exchange-mobile">' +
      (state.eligible
        ? '<button class="js-return-exchange order-footer-btn"' + attrs + '>Return / Exchange</button>' +
          '<p class="return-exchange-label">Return / Exchange item: Eligible till ' + escapeHtml(state.windowEndLabel) + '</p>'
        : '<button class="js-return-exchange disabled" disabled' + attrs + '>Return / Exchange (Closed)</button>' +
          '<p class="return-exchange-label">Return window closed on ' + escapeHtml(state.windowEndLabel) + '</p>') +
    '</div>';
  }

  /** Liquid passes customer.email, which is empty for KwikPass sessions. */
  function returnExchangeEmail() {
    return displaySafe(
      (window.Shopify && window.Shopify.customer && window.Shopify.customer.email) ||
      (kpDetailsRecord && kpDetailsRecord.email) ||
      (payloadRecord && payloadRecord.email) || ''
    );
  }

  function ctaHtml(order) {
    var showCancel = false;
    if (!order.cancelled) {
      if (String(order.fulfillmentStatus || '').toUpperCase() === 'UNFULFILLED') showCancel = true;
      else if (order.shipmentDetails && order.shipmentCount === 0) showCancel = true;
    }
    if (order.isMigrated) showCancel = false;

    var full = showCancel ? '' : ' full-width';
    var html = '<div class="order-footer-btns' + (showCancel ? '' : ' single-btn') + '">' +
      '<a href="/pages/return-policy"' + (showCancel ? '' : ' class="full-width"') + '>' +
        '<button class="order-footer-btn' + full + '">View Return Policy</button>' +
      '</a>';
    if (showCancel) {
      html += '<button attrorder="' + escapeHtml(numericId(order.id)) + '" ' +
              'class="order-footer-btn custom-canclebtn tocancelbtn sub">Cancel Order</button>';
    }
    // main-order.liquid:3441 wraps the Return / Exchange box and the buttons in
    // .order-details-footer, which lays them out side by side on desktop.
    return '<div class="order-details-footer">' +
             (order.cancelled ? '' : returnExchangeHtml(order)) +
             html + '</div>' +
           '</div>';
  }

  /**
   * main-order.liquid:3276-3287 — a standalone promo block shown once anything
   * has been delivered. It links to the storefront; it does not re-add to cart.
   */
  function buyAgainHtml(order) {
    var shipments = (order.shipmentDetails && order.shipmentDetails.shipments) || [];
    var delivered = shipments.some(function (sh) { return sh.status === 'DELIVERED'; }) ||
                    order.deliveryStatus === 'DELIVERED';
    if (!delivered) return '';
    return '<div class="order-buy-again-section mobile-view">' +
             '<p class="order-buy-again-text">Good things deserve a repeat!</p>' +
             '<a href="/" class="order-buy-again-btn">' +
               '<span class="order-buy-again-btn-text">Buy again</span></a>' +
           '</div>';
  }

  var INFO_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" class="shipping-info-icon">' +
    '<circle cx="7" cy="7" r="6.5" stroke="#727272"/>' +
    '<path d="M7 6V10" stroke="#727272" stroke-linecap="round"/>' +
    '<circle cx="7" cy="4" r="0.75" fill="#727272"/></svg>';

  function SHIPPING_TOOLTIP(fee, threshold) {
    return '<div class="shipping-info-tooltip-wrapper">' + INFO_ICON +
             '<div class="shipping-info-tooltip"><div class="shipping-info-tooltip-content">' +
               'Orders below \u20B9' + threshold + ' include a \u20B9' + fee +
               ' shipping fee. Cash on Delivery orders include an additional \u20B9' + fee +
               ' collection fee.' +
             '</div></div></div>';
  }

  var REFUND_ICON = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\"> <path d=\"M2.9996 9.29995H20.9996M6.5996 13.4999H9.5996M4.80006 5.1001H19.1997C20.5251 5.1001 21.5996 6.17377 21.5997 7.49923L21.5999 16.5011C21.6 17.8266 20.5255 18.9001 19.2 18.9001L4.80029 18.8999C3.47485 18.8999 2.40035 17.8254 2.40032 16.5L2.40006 7.50016C2.40002 6.17465 3.47455 5.1001 4.80006 5.1001Z\" stroke=\"black\" stroke-width=\"1.6\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> </svg>";
  var REFUND_TICK = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" viewBox=\"0 0 24 24\" fill=\"none\" > <path d=\"M4 12L9 17L20 6\" stroke=\"white\" stroke-width=\"3\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/> </svg>";

  /** Liquid uses money_with_currency here, which always shows paise. */
  function formatMoneyExact(amount) {
    var n = Number(amount);
    if (Number.isNaN(n)) return '';
    return '\u20B9' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatDateTime(value) {
    if (!value) return '';
    var d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  /**
   * Port of the refund card in main-order.liquid:2942-3005. Refunds get their
   * own section above the cart items — squeezing them into the price summary
   * loses the processed badge, the timing copy and the dispute reference.
   */
  function refundDetailsHtml(order) {
    var isRefunded = order.financialStatus === 'REFUNDED' || order.financialStatus === 'PARTIALLY_REFUNDED';
    var isReturned = order.returnStatus === 'RETURNED';
    // Never shown for COD. A COD parcel that comes back undelivered was never
    // paid for, so a "Refund details" block (with "Total amount to be refunded")
    // promises money the customer never gave us. Product decision, KwikPass path
    // only — main-order.liquid still shows it on classic sessions.
    var isCod = String(order.paymentMethod || '').toUpperCase() === 'COD';
    if (order.isMigrated || isCod || (!isRefunded && !isReturned)) return '';

    var amount = isRefunded ? order.refunded : order.total;
    var label = isRefunded ? 'Amount refunded' : 'Total amount to be refunded';

    var html =
      '<div class="order-refund-section">' +
        '<div class="refund-section-header">' + REFUND_ICON + '<span>Refund details</span></div>' +
        '<div class="refund-total-row"><span>' + label + '</span>' +
          '<span class="refund-amount">' + formatMoneyExact(amount) + '</span></div>';

    if (isRefunded) {
      html += '<div class="refund-processed-status">' +
                '<div class="refund-processed-icon">' + REFUND_TICK + '</div>' +
                '<span class="refund-processed-text">Refund Processed</span>' +
              '</div>';
    }

    // Nothing refunded yet: the Liquid still prints "Your refund of ₹0.00 has
    // been processed" here, which is false. Say what will happen instead, using
    // the amount the headline promises. (order.refunded arrives as "0.0", a
    // truthy string, which is how ₹0.00 slipped through before.)
    var refundedAmount = Number(order.refunded) || 0;
    var when = order.refundedAt;
    var message;
    if (refundedAmount > 0) {
      message = 'Your refund of <span class="refund-highlight">' + formatMoneyExact(refundedAmount) + '</span> ' +
        (when
          ? 'was processed successfully on ' + escapeHtml(formatDateLong(when)) + ' ' +
            escapeHtml(formatDateTime(when)) +
            '. It usually takes 3\u20137 business days for the funds to appear in your original payment method.'
          : 'has been processed and it will reflect in the original mode of payment.');
    } else {
      message = 'Your refund of <span class="refund-highlight">' + formatMoneyExact(amount) + '</span> ' +
        'will be processed to your original mode of payment.';
    }
    html += '<div class="refund-message-box"><p>' + message + '</p></div>';

    if (order.refundRrn) {
      html += '<div class="refund-dispute-row">&#9432;<span>Have a dispute? Contact your bank with ' +
              'refund transaction reference number ' + escapeHtml(order.refundRrn) + '</span></div>';
    }

    return html + '</div>';
  }

  /** Ids of the line items in one shipment, plus their per-shipment quantities. */
  function shipmentItemIndex(shipment) {
    var ids = [], qty = {};
    (shipment.fulfilledItems || []).forEach(function (fi) {
      var id = String(fi.id || fi.lineItemId || '');
      if (!id) return;
      ids.push(id);
      if (fi.quantity != null) qty[id] = fi.quantity;
    });
    return { ids: ids, qty: qty };
  }

  /** One tab panel: tracker + the items in that shipment. */
  function shipmentPanelHtml(order, shipment, index) {
    var st = shipmentState(order, shipment);
    var idx = shipmentItemIndex(shipment);
    var body = progressBarHtml(st);
    if (st.isShipped && !st.isDelivered && !st.isReturned && !st.isCancelled) {
      body += '<div class="not-cancelled-text">Orders once shipped cannot be cancelled</div>';
    }
    // main-order.liquid:2488 — mobile-only dashed rule between tracker and items.
    body += '<div class="dashed-line mobile-view"></div>';
    body += lineItemsHtml(order, idx.ids.length ? idx.ids : null, idx.qty);
    // All sections render stacked, as in main-order.liquid:2452 — the tabs scroll
    // to them rather than swapping one in and hiding the rest.
    return '<div id="shipment-' + (index + 1) + '" class="order-shipment-section" data-kp-panel="' + index + '">' +
           body + '</div>';
  }

  var PENDING_ETA_SECONDS = 432000; // 5 days, per main-order.liquid:2595

  function pendingEta(createdAt) {
    var t = new Date(createdAt).getTime();
    if (Number.isNaN(t)) return '';
    return new Date(t + PENDING_ETA_SECONDS * 1000).toISOString();
  }

  /**
   * Pending items have not shipped, so the tracker is a fresh 4-step bar —
   * main-order.liquid:2606-2621 hardcodes shipped/out-for-delivery/delivered/
   * returned to false and only carries the confirmed and expected dates. Reusing
   * the order-level state here wrongly inherits a sibling shipment's outcome.
   */
  function pendingPanelHtml(order, pendingItems, index) {
    var ids = pendingItems.map(function (p) { return String(p.id); });
    var qty = {};
    pendingItems.forEach(function (p) { if (p.quantity != null) qty[String(p.id)] = p.quantity; });

    var st = {
      isCancelled: !!order.cancelled,
      isReturned: false, isDelivered: false, isOutForDelivery: false, isShipped: false,
      isCod: String(order.paymentMethod || '').toUpperCase() === 'COD',
      isMigrated: !!order.isMigrated,
      isDelayed: false,
      confirmedDate: pbDate(order.createdAt),
      shippedDate: '', outForDeliveryDate: '', deliveredDate: '', returnDate: '',
      cancelledDate: pbDate(order.canceledAt),
      cancellationReason: order.cancelReason || '',
      failedDeliveryReason: '',
      // NOT the estimated_delivery_date metafield — that belongs to the shipped
      // parcels. main-order.liquid:2595 computes this panel's date as
      // created_at + 432000s (5 days), since pending items have no dispatch yet.
      expectedDeliveryDate: pbDate(pendingEta(order.createdAt)),
      trackingNumber: '', trackingUrl: '', latestTrackingNote: '', invoiceUrl: '',
      showShipmentTabs: orderDisplayStatus(order).partial,
      orderTotal: order.total,
      orderId: numericId(order.id),
      totalSteps: order.cancelled ? 2 : 4,
      completedSteps: order.cancelled ? 2 : 1,
      percentage: 0
    };

    // lineItemsHtml already emits the "Cart item (N)" header — do not add a second.
    // main-order.liquid:2612 wraps the pending panel's contents in this block,
    // which carries the 24px vertical padding.
    return '<div id="pending-items" class="order-shipment-section order-shipment-section-pending" data-kp-panel="' + index + '">' +
             '<div class="order-pending-items-block">' +
               progressBarHtml(st) +
               lineItemsHtml(order, ids, qty) +
             '</div>' +
           '</div>';
  }

  function tabsHtml(labels) {
    if (labels.length < 2) return '';
    return '<div class="order-shipment-tabs" role="tablist"><div class="order-shipment-tabs-track">' +
      labels.map(function (label, i) {
        return '<button type="button" role="tab" class="order-shipment-tab' +
               (i === 0 ? ' is-active' : '') + '" data-kp-tab="' + i + '"' +
               ' aria-selected="' + (i === 0 ? 'true' : 'false') + '">' + escapeHtml(label) + '</button>';
      }).join('') +
      '</div><div class="order-shipment-tabs-underline is-hidden" aria-hidden="true"></div></div>';
  }

  /**
   * The active-tab underline is positioned by script, not CSS — it is absolutely
   * placed and gets its width/offset from the active tab. Ported from
   * main-order.liquid's setActiveTab(); without this it stays 0-wide at the left
   * edge and the active tab looks unhighlighted.
   */
  function positionTabUnderline(root, tab) {
    var tabsEl = root.querySelector('.order-shipment-tabs');
    var underline = tabsEl && tabsEl.querySelector('.order-shipment-tabs-underline');
    if (!tabsEl || !underline || !tab) return;
    var tabsRect = tabsEl.getBoundingClientRect();
    var tabRect = tab.getBoundingClientRect();
    if (!tabRect.width) return;
    underline.style.width = tabRect.width + 'px';
    underline.style.transform = 'translateX(' + (tabRect.left - tabsRect.left) + 'px)';
    underline.classList.remove('is-hidden');
  }

  /**
   * Port of main-order.liquid:4187-4240. Every section stays on the page; a tab
   * click smooth-scrolls to its section, and an IntersectionObserver moves the
   * active tab as the customer scrolls through them.
   */
  var SHIPMENT_TAB_SCROLL_OFFSET = 116; // main-order.liquid:4198

  function setActiveShipmentTab(root, index) {
    var active = null;
    root.querySelectorAll('[data-kp-tab]').forEach(function (t) {
      var on = Number(t.getAttribute('data-kp-tab')) === index;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) active = t;
    });
    positionTabUnderline(root, active);
  }

  function bindOrderDetail(root, order) {
    root.querySelectorAll('[data-kp-tab]').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var index = Number(tab.getAttribute('data-kp-tab'));
        var section = root.querySelector('[data-kp-panel="' + index + '"]');
        if (!section) return;
        var top = section.getBoundingClientRect().top + window.scrollY - SHIPMENT_TAB_SCROLL_OFFSET;
        window.scrollTo({ top: top, behavior: 'smooth' });
        setActiveShipmentTab(root, index);
      });
    });

    var track = root.querySelector('.order-shipment-tabs-track');
    if (track) {
      // The track scrolls sideways when there are many tabs; keep the underline
      // pinned to the active one as it moves.
      track.addEventListener('scroll', function () {
        positionTabUnderline(root, root.querySelector('[data-kp-tab].is-active'));
      });
    }

    // A re-render replaces the sections, so drop the previous observer first.
    if (root.__kpShipmentObserver) {
      root.__kpShipmentObserver.disconnect();
      root.__kpShipmentObserver = null;
    }
    var sections = root.querySelectorAll('[data-kp-panel]');
    if (root.querySelector('[data-kp-tab]') && sections.length && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          setActiveShipmentTab(root, Number(entry.target.getAttribute('data-kp-panel')));
        });
      }, { root: null, rootMargin: '-20% 0px -60% 0px', threshold: 0 });
      sections.forEach(function (section) { observer.observe(section); });
      root.__kpShipmentObserver = observer;
    }

    // Place it under the initially-active tab. rAF so layout has settled —
    // measuring straight after innerHTML gives a zero-width rect.
    var active = root.querySelector('[data-kp-tab].is-active');
    if (active) {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(function () { positionTabUnderline(root, active); });
      } else {
        positionTabUnderline(root, active);
      }
      // Tab widths change with the viewport, so keep it aligned on resize.
      if (!root.__kpTabResize) {
        root.__kpTabResize = function () {
          positionTabUnderline(root, root.querySelector('[data-kp-tab].is-active'));
        };
        window.addEventListener('resize', root.__kpTabResize);
      }
    }

    // "See all updates". order-tracking-update.liquid binds its links on
    // DOMContentLoaded, before this render, and its popup reads the tracking
    // number from the first .delivery-updates-section on the page. The KwikPass
    // branch of main-order.liquid renders exactly one; point it at the clicked
    // shipment, then open it.
    root.querySelectorAll('.js-open-delivery-popup').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var section = document.querySelector('.delivery-updates-section');
        if (!section || !window.Shopify || typeof window.Shopify.openDeliveryPopup !== 'function') return;
        section.setAttribute('data-tracking-number', link.getAttribute('data-tracking-number') || '');
        section.setAttribute('data-order-id', link.getAttribute('data-order-id') || '');
        var edd = document.getElementById('deliveryEdd');
        if (edd) edd.textContent = link.getAttribute('data-edd') || 'Loading...';
        window.Shopify.openDeliveryPopup(event);
      });
    });

    // Return / Exchange. returnprime_script.liquid binds .js-return-exchange on
    // DOMContentLoaded, before this render, so the same request is made here:
    // ReturnPrime resolves the order + email and returns the portal URL.
    root.querySelectorAll('.js-return-exchange').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        if (btn.disabled || btn.classList.contains('disabled') || btn.classList.contains('rp_btn_loading')) return;
        var email = returnExchangeEmail() || btn.getAttribute('data-customer') || '';
        btn.classList.add('rp_btn_loading');
        fetch('https://api.returnprime.co/fetch_order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            order_number: btn.getAttribute('data-order'),
            email: email,
            store: (window.Shopify && window.Shopify.shop) || config().shopDomain
          })
        })
          .then(function (response) { return response.json(); })
          .then(function (response) {
            btn.classList.remove('rp_btn_loading');
            if (response.success === true && response.url) window.location.href = response.url;
            else if (response.success === false) window.alert(response.message);
          })
          .catch(function (error) {
            btn.classList.remove('rp_btn_loading');
            window.alert(error.message);
          });
      });
    });

    // order-undelivered-popup.liquid binds on DOMContentLoaded, long before this
    // async render. Shopify.openUndeliveredPopup is global, so wire it directly.
    root.querySelectorAll('.js-open-undelivered-popup').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        if (window.Shopify && typeof window.Shopify.openUndeliveredPopup === 'function') {
          window.Shopify.openUndeliveredPopup();
        }
      });
    });

    var buyAgain = root.querySelector('[data-kp-buy-again]');
    if (buyAgain) {
      buyAgain.addEventListener('click', function () {
        var all = order.lineItems || [];
        var items = all.filter(function (i) { return i.purchasable; });
        var skipped = all.length - items.length;
        if (!items.length) {
          buyAgain.disabled = true;
          buyAgain.textContent = 'No longer available';
          return;
        }
        buyAgain.disabled = true;
        var original = buyAgain.textContent;
        buyAgain.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: items.map(function (i) {
              return { id: numericId(i.variantId), quantity: Number(i.quantity) || 1 };
            })
          })
        })
          .then(function (r) {
            if (!r.ok) {
              return r.json().catch(function () { return {}; }).then(function (body) {
                throw new Error(body.description || body.message || 'add to cart failed');
              });
            }
            return r.json();
          })
          .then(function () {
            if (skipped) {
              // Tell the customer before navigating — silently dropping items
              // from their cart is worse than a slightly slower redirect.
              console.warn('[KpAccount] buy again skipped ' + skipped + ' unavailable item(s)');
            }
            window.location.href = '/cart';
          })
          .catch(function (error) {
            console.warn('[KpAccount] buy again failed', error);
            buyAgain.disabled = false;
            buyAgain.textContent = original || 'Buy again';
            var msg = document.createElement('p');
            msg.className = 'cancelled-reason-text';
            msg.textContent = 'We could not add these to your cart. Please try again.';
            if (buyAgain.parentNode && !buyAgain.parentNode.querySelector('.kp-buy-again-error')) {
              msg.classList.add('kp-buy-again-error');
              buyAgain.parentNode.appendChild(msg);
            }
          });
      });
    }
  }

  function renderOrderDetail(root, orderId) {
    if (!root) return;
    var orders = ordersCache || [];
    var order = orders.filter(function (item) {
      return item.id === orderId ||
             numericId(item.id) === numericId(orderId) ||
             String(item.orderNumber) === String(orderId) ||
             item.name === orderId;
    })[0];

    if (!order) {
      root.innerHTML =
        '<p class="hdt-account-hello">We could not find this order.</p>' +
        '<p><a href="' + escapeHtml(ordersPath()) + '">Back to orders</a></p>';
      return;
    }

    var shipments = (order.shipmentDetails && order.shipmentDetails.shipments) || [];
    var pending = (order.shipmentDetails && order.shipmentDetails.pendingItems) || [];

    var labels = shipments.map(function (_, i) { return 'Shipment ' + (i + 1); });
    if (pending.length) labels.push('Pending Items');

    // main-order.liquid:2576 and :2726 emit a section-border after every shipment
    // and after the pending panel — that grey gap is what makes each one its own
    // card. The last panel's border comes from the blocks join below, so only
    // the ones between panels are added here.
    var panelList = shipments.map(function (s, i) { return shipmentPanelHtml(order, s, i); });
    if (pending.length) panelList.push(pendingPanelHtml(order, pending, shipments.length));
    var panels = panelList.join(sectionBorder());
    // No shipment_details at all: fall back to a single order-level tracker.
    // Order-level fallback. main-order.liquid:2939-3021 puts the refund block
    // between the tracker and the cart items, each separated by a section-border.
    var orderLevel = !panels;
    var orderLevelItems = '';
    if (orderLevel) {
      panels = '<div class="order-shipment-section" data-kp-panel="0">' +
                 progressBarHtml(shipmentState(order, null)) +
               '</div>';
      orderLevelItems = lineItemsHtml(order, null, null);
    }

    // Liquid keeps the greeting, order meta and tracker in ONE card with no
    // dividers between them (main-order.liquid:2200-2440). Dividers only appear
    // between the larger sections, and only where both sides actually render.
    var summaryCard =
      orderStatusHeadingHtml(order) +
      '<div class="line-items-div"><div class="order-place-details">' +
        '<p class="placed-text">Order Placed On : <span>' + escapeHtml(formatDate(order.createdAt)) + '</span></p>' +
        '<p class="placed-text">Order ID : <span>' + escapeHtml(order.name) + '</span></p>' +
      '</div></div>' +
      cashbackHtml(order) +
      tabsHtml(labels) + panels;

    var blocks = [
      summaryCard,
      // main-order.liquid:2939-3021 — refund sits between tracker and cart items.
      refundDetailsHtml(order),
      orderLevelItems,
      buyAgainHtml(order),
      addressSubtotalHtml(order)
    ].filter(Boolean);

    root.innerHTML = blocks.join(sectionBorder());

    bindOrderDetail(root, order);
  }

  var bootState = { promise: null, done: false };

  /**
   * Called from init(), handleKpToken() and the SDK-token callback, so it can
   * fire three times on one page load. Collapse those into a single run;
   * pass force:true only when the session genuinely changed (login/logout).
   */
  function bootAccountPage(force) {
    if (force) {
      bootState.promise = null;
      bootState.done = false;
    }
    if (bootState.done) return Promise.resolve();
    if (bootState.promise) return bootState.promise;
    bootState.promise = bootAccountPageOnce().then(function () {
      bootState.done = true;
      bootState.promise = null;
    }, function (error) {
      bootState.promise = null;
      throw error;
    });
    return bootState.promise;
  }

  /**
   * Holds the theme's inter-page shimmer up until our data has rendered.
   *
   * me-page-transition deliberately has no hide-on-arrival logic — first paint
   * of the new document is the hide. That is right for Liquid pages, which
   * arrive fully rendered, but these pages arrive empty and then fetch, so the
   * customer saw shimmer -> skeleton -> content. Re-showing it on arrival and
   * hiding it after render collapses that into one continuous loading state.
   *
   * show() resets an 8s safety timer inside the overlay, so a hung request
   * still clears itself rather than trapping the page behind the shimmer.
   */
  function pageOverlay() {
    var api = window.MEPageTransition;
    return api && typeof api.show === 'function' && typeof api.hide === 'function' ? api : null;
  }

  /**
   * me-page-transition.js is loaded with `defer`, so when kp-loading-overlay
   * runs inline during parse the module does not exist yet and the snippet sets
   * the is-visible class directly. The module's internal `visible` flag stays
   * false, and its hide() opens with `if (!visible) return;` — so calling the
   * API alone leaves the overlay on screen forever.
   *
   * Call the API (to clear its safety timer and sync its state) AND drop the
   * class ourselves, so the overlay clears whichever path put it up.
   */
  function hidePageOverlay() {
    // Tells kp-loading-overlay's deferred upgrade() not to re-show it if the
    // render beat DOMContentLoaded.
    window.__kpOverlayHidden = true;
    var api = pageOverlay();
    if (api) {
      try { api.hide(); } catch (e) {}
    }
    var el = document.getElementById('me-page-transition');
    if (el) {
      el.classList.remove('is-visible');
      el.setAttribute('aria-hidden', 'true');
    }
  }

  async function bootAccountPageOnce() {
    var profileRoot = document.getElementById('kp-account-profile');
    var ordersRoot = document.getElementById('kp-order-list');
    var orderRoot = document.getElementById('kp-order-detail');
    if (!profileRoot && !ordersRoot && !orderRoot) return;

    // NOT shown here — the kp-loading-overlay snippet already showed it inline,
    // before first paint. Calling show() at this point is what produced the
    // shimmer -> skeleton -> shimmer bounce, because the skeletons paint first.

    // Skeletons sit underneath: what the customer sees if the overlay is
    // unavailable, or once its safety timeout fires.
    if (profileRoot) showLoader(profileRoot, 'Loading your account…');
    if (ordersRoot) showLoader(ordersRoot, 'Loading your orders…');
    if (orderRoot) showLoader(orderRoot, 'Loading order details…');

    try {
      await bootAccountPageBody(profileRoot, ordersRoot, orderRoot);
    } finally {
      // Every exit path — rendered, not-logged-in, or thrown — must clear it.
      hidePageOverlay();
    }
  }

  async function bootAccountPageBody(profileRoot, ordersRoot, orderRoot) {

    if (!payloadRecord) {
      var local = await localKpIdentity();
      await applyPayload(normalizePayload({
        customerAccessToken: currentAccessToken(),
        customerId: local.customerId,
        email: local.email,
        phone: local.phone
      }));
    }

    if (!requireLogin(window.location.pathname + window.location.search)) {
      if (profileRoot) profileRoot.innerHTML = '<p>Please log in to view your account.</p>';
      if (ordersRoot) ordersRoot.innerHTML = '<p>Please log in to view your orders.</p>';
      if (orderRoot) orderRoot.innerHTML = '<p>Please log in to view this order.</p>';
      return;
    }

    if (profileRoot) {
      try {
        await fetchKpCustomerDetails(true);
      } catch (error) {
        if (error.code === 'FORBIDDEN' || error.code === 'UNAUTHENTICATED') {
          showError(profileRoot, 'We could not verify your session. Please log in again.');
          return;
        }
        // Any other failure still renders from token/local data below.
      }
    }

    // The order-detail page resolves its own order via fetchKpOrderDetail, so it
    // does not need the whole list. Only fetch it where something renders it.
    if (ordersRoot) {
      try {
        await fetchOrders(true);
      } catch (error) {
        console.warn('[KpAccount] Orders fetch failed', error);
        ordersLoadError = ordersLoadError || 'fetch-failed';
      }
    }

    await logKpDebug('account page boot', {
      payload: payloadRecord,
      customer: customerRecord,
      note: !storefrontToken()
        ? 'Storefront API token is empty in theme settings, so firstName/lastName cannot be loaded.'
        : !currentAccessToken()
          ? 'No customerAccessToken (acs_tkn/cap_tkn), so Storefront customer query was skipped.'
          : 'Storefront query ran. If firstName is empty, the Shopify customer record has no name.'
    });

    if (profileRoot) await renderProfile(profileRoot);
    if (ordersRoot) renderOrderList(ordersRoot);
    if (orderRoot) {
      var params = new URLSearchParams(window.location.search);
      var requestedOrderId =
        params.get('id') || params.get('order') || orderIdFromLegacyPath();
      var detailLoaded = false;
      if (requestedOrderId && kpGraphqlUrl()) {
        try {
          var detail = await fetchKpOrderDetail(requestedOrderId);
          if (detail) {
            cacheOrder(detail);
            detailLoaded = true;
          }
        } catch (error) {
          if (error.code === 'FORBIDDEN' || error.code === 'UNAUTHENTICATED') {
            showError(orderRoot, 'We could not verify your session. Please log in again.');
            return;
          }
          console.warn('[KpAccount] orderDetail failed, falling back to list data', error);
        }
      }
      // Only pay for the list request when the direct lookup did not work.
      if (!detailLoaded && !ordersCache) {
        try {
          await fetchOrders(true);
        } catch (error) {
          console.warn('[KpAccount] Orders fallback fetch failed', error);
          ordersLoadError = ordersLoadError || 'fetch-failed';
        }
      }
      renderOrderDetail(orderRoot, requestedOrderId);
    }
  }

  function interceptAccountLinks() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      var path = href.split('?')[0];
      if (path === '/account' || path === '/account/' || path === '/account/login') {
        event.preventDefault();
        if (isLoggedIn() && path.indexOf('/account/login') === -1) {
          window.location.href = accountPath();
        } else {
          login(accountPath());
        }
      }
      if (path === '/account/logout' || path.indexOf('/account/logout') !== -1) {
        event.preventDefault();
        logout();
      }
    });
  }

  function bindEvents() {
    window.addEventListener('kp_data_sent', function (event) {
      var detail = event.detail || {};
      kpLog('[KpAccount] event kp_data_sent', {
        success: detail.success,
        kpLogout: detail.kpLogout,
        error: detail.error,
        kpToken: redactToken(detail.kpToken),
        detailKeys: Object.keys(detail)
      });
      if (detail.kpLogout) {
        clearSessionUi();
        window.dispatchEvent(new CustomEvent('kp-customer-logout'));
        return;
      }
      if (detail.kpToken) handleKpToken(detail.kpToken);
    });

    window.addEventListener('account_access_token', function (event) {
      var token = event.detail && event.detail.token;
      kpLog('[KpAccount] event account_access_token', {
        hasToken: !!token,
        token: redactToken(token),
        detail: snapshotObject(event.detail, 0)
      });
      if (!token) return;
      // No order fetch here: this fires on whatever page the login happened on.
      // bootAccountPage(true) below fetches orders only where a list renders.
      applyPayload(normalizePayload({ customerAccessToken: token, customerId: readStorage('kp_customer_id') })).then(function () {
        window.dispatchEvent(new CustomEvent('kp-customer-ready', { detail: { payload: payloadRecord, customer: customerRecord } }));
        resolveReady();
        return bootAccountPage(true);
      }).catch(function () {});
    });

    window.addEventListener('user-loggedin', function () {
      requestStoredKpToken().then(function () { bootAccountPage(); });
    });

    window.addEventListener('nector:data-saved', function (event) {
      var available = event.detail && event.detail.available;
      if (available == null) return;
      document.querySelectorAll('[data-kp-mamacash]').forEach(function (el) {
        el.textContent = String(available);
      });
    });
  }

  async function init() {
    document.body.classList.add(isLoggedIn() ? 'kp-logged-in' : 'kp-guest');
    interceptAccountLinks();
    bindEvents();

    if (window.merchantInfo) {
      window.merchantInfo.customAccountPagePath = accountPath();
    }

    var existing = readTokenValue('kpToken');
    if (existing) {
      await handleKpToken(existing);
    } else if (currentAccessToken() || readStorage('kp_customer_id') || kwikSessionToken()) {
      var local = await localKpIdentity();
      await applyPayload(normalizePayload({
        customerAccessToken: currentAccessToken(),
        customerId: local.customerId,
        email: local.email,
        phone: local.phone
      }));
      // Same reasoning as handleKpToken: no speculative order fetch on pages
      // that will never render an order list.
      resolveReady();
    }

    try {
      await requestStoredKpToken();
    } catch (e) {}

    await bootAccountPage();
  }

  window.KpAccount = {
    /**
     * Bump when the queries or render layer change. Lets you confirm from the
     * console which build the storefront is actually serving — a live pull can
     * silently replace this file with the deployed one.
     *   KpAccount.version
     */
    version: '6.10.1-cashback',
    /** The exact queries this build sends, for debugging deploys. */
    orderDetailQuery: function () { return KP_ORDER_DETAIL_QUERY; },
    ordersQuery: function () { return KP_ORDERS_QUERY; },
    getPayload: function () { return payloadRecord; },
    getCustomer: function () { return customerRecord || (window.Shopify && window.Shopify.customer); },
    isLoggedIn: isLoggedIn,
    login: login,
    logout: logout,
    requireLogin: requireLogin,
    onApiError: onApiError,
    fetchCustomer: fetchCustomer,
    fetchOrders: fetchOrders,
    loadMoreOrders: loadMoreOrders,
    ordersPageInfo: function () { return ordersPageInfo; },
    kpGraphql: kpGraphql,
    kpToken: kpTokenValue,
    fetchCustomerDetails: fetchKpCustomerDetails,
    getCustomerDetails: function () { return kpDetailsRecord; },
    fetchKpOrders: fetchKpOrders,
    fetchOrderDetail: fetchKpOrderDetail,
    whenReady: whenReady,
    accountPath: accountPath,
    ordersPath: ordersPath,
    handleKpToken: handleKpToken,
    debugDump: function () { return logKpDebug('manual dump'); },
    renderProfile: renderProfile,
    renderOrderList: renderOrderList,
    renderOrderDetail: renderOrderDetail
  };

  window.getCustomerId = function () {
    if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) return window.Shopify.customer.id;
    var stored = readStorage('kp_customer_id');
    if (stored && /^\d{5,}$/.test(String(stored))) return stored;
    return stored && !looksLikeCiphertext(stored) ? stored : null;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
